/**
 * CRM API Service
 * Handles all API calls to the CRM microservice
 */

import api from './api';

const CRM_BASE_URL = '/crm';
const PUBLIC_BASE_URL = '/crm/public';

// ============================================================================
// CUSTOMER MANAGEMENT
// ============================================================================

/**
 * Get all customers with pagination and filters
 * @param {Object} params - Query parameters
 * @returns {Promise} Customer list response
 */
export const getCustomers = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/customers`, { params });
};

/**
 * Get all customers (alias for getCustomers)
 * @param {Object} params - Query parameters
 * @returns {Promise} Customer list response
 */
export const getAllCustomers = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/customers`, { params });
};

/**
 * Get customer by ID with 360-degree view
 * @param {string} id - Customer ID
 * @returns {Promise} Customer details
 */
export const getCustomerById = (id) => {
  return api.get(`${CRM_BASE_URL}/customers/${id}`);
};

/**
 * Create new customer
 * @param {Object} customerData - Customer data
 * @returns {Promise} Created customer
 */
export const createCustomer = (customerData) => {
  return api.post(`${CRM_BASE_URL}/customers`, customerData);
};

/**
 * Update customer
 * @param {string} id - Customer ID
 * @param {Object} customerData - Updated customer data
 * @returns {Promise} Updated customer
 */
export const updateCustomer = (id, customerData) => {
  return api.put(`${CRM_BASE_URL}/customers/${id}`, customerData);
};

/**
 * Archive customer (soft delete)
 * @param {string} id - Customer ID
 * @returns {Promise} Success message
 */
export const archiveCustomer = (id) => {
  return api.delete(`${CRM_BASE_URL}/customers/${id}`);
};

/**
 * Delete customer (soft delete - alias for archiveCustomer)
 * @param {string} id - Customer ID
 * @returns {Promise} Success message
 */
export const deleteCustomer = (id) => {
  return api.delete(`${CRM_BASE_URL}/customers/${id}`);
};

/**
 * Get customer statistics
 * @returns {Promise} Customer statistics
 */
export const getCustomerStats = (config = {}) => {
  return api.get(`${CRM_BASE_URL}/customers/stats`, config);
};

/**
 * Get customer contacts
 * @param {string} customerId - Customer ID
 * @returns {Promise} Customer contacts
 */
export const getCustomerContacts = (customerId) => {
  return api.get(`${CRM_BASE_URL}/customers/${customerId}/contacts`);
};

/**
 * Get contacts by customer (alias for getCustomerContacts)
 * @param {string} customerId - Customer ID
 * @returns {Promise} Customer contacts
 */
export const getContactsByCustomer = (customerId) => {
  return api.get(`${CRM_BASE_URL}/customers/${customerId}/contacts`);
};

/**
 * Get all contacts
 * @param {Object} params - Query parameters
 * @returns {Promise} Contacts list
 */
export const getAllContacts = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/contacts`, { params });
};

/**
 * Add contact to customer
 * @param {string} customerId - Customer ID
 * @param {Object} contactData - Contact data
 * @returns {Promise} Created contact
 */
export const addCustomerContact = (customerId, contactData) => {
  return api.post(`${CRM_BASE_URL}/customers/${customerId}/contacts`, contactData);
};

// ============================================================================
// LEAD MANAGEMENT
// ============================================================================

/**
 * Get all leads with filters (location-based only, no user filter)
 * @param {Object} params - Query parameters
 * @returns {Promise} Lead list
 */
export const getLeads = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/leads`, { params });
};

/**
 * Get all leads without user filtering (for Leads page - location-based only)
 * @param {Object} params - Query parameters
 * @returns {Promise} Lead list
 */
export const getAllLeads = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/leads`, { params: { ...params, myLeads: 'false' } });
};

/**
 * Get leads for My Leads page (assigned to user; super admin sees all for location)
 * @param {Object} params - Query parameters
 * @returns {Promise} Lead list
 */
export const getMyLeads = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/leads/my-leads`, { params });
};

/**
 * Get lead by ID
 * @param {string} id - Lead ID
 * @returns {Promise} Lead details
 */
export const getLeadById = (id) => {
  return api.get(`${CRM_BASE_URL}/leads/${id}`);
};

/**
 * Create new lead
 * @param {Object} leadData - Lead data
 * @returns {Promise} Created lead
 */
export const createLead = (leadData) => {
  return api.post(`${CRM_BASE_URL}/leads`, leadData);
};

/**
 * Bulk upload leads from CSV file
 * @param {File} file - CSV file
 * @param {string} locationId - Location ID for the leads
 * @param {string} leadPoolId - Lead pool ID for the leads
 * @param {string} subProductId - Optional sub product ID for the leads
 * @returns {Promise} Upload result with success/error counts
 */
export const bulkUploadLeads = (file, locationId, leadPoolId, subProductId, assignedTo) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('locationId', locationId);
  formData.append('leadPoolId', leadPoolId);
  if (subProductId) {
    formData.append('subProductId', subProductId);
  }
  if (assignedTo) {
    formData.append('assignedTo', assignedTo);
  }
  
  return api.post(`${CRM_BASE_URL}/leads/bulk-upload`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * Update lead
 * @param {string} id - Lead ID
 * @param {Object} leadData - Updated lead data
 * @returns {Promise} Updated lead
 */
export const updateLead = (id, leadData) => {
  return api.put(`${CRM_BASE_URL}/leads/${id}`, leadData);
};

/**
 * Convert lead to customer
 * @param {string} id - Lead ID
 * @param {Object} options - Conversion options (e.g., { createOpportunity: true })
 * @returns {Promise} Created customer
 */
export const convertLead = (id, options = {}) => {
  return api.post(`${CRM_BASE_URL}/leads/${id}/convert`, options);
};

/**
 * Qualify lead
 * @param {string} id - Lead ID
 * @param {Object} qualificationData - Qualification criteria
 * @returns {Promise} Qualified lead
 */
export const qualifyLead = (id, qualificationData) => {
  return api.post(`${CRM_BASE_URL}/leads/${id}/qualify`, qualificationData);
};

/**
 * Archive/Delete lead (soft delete)
 * @param {string} id - Lead ID
 * @returns {Promise} Success message
 */
export const archiveLead = (id) => {
  return api.delete(`${CRM_BASE_URL}/leads/${id}`);
};

/**
 * Bulk delete leads
 * @param {string[]} leadIds - Array of lead IDs to delete
 * @returns {Promise} Delete result
 */
export const bulkDeleteLeads = (leadIds) => {
  return api.delete(`${CRM_BASE_URL}/leads/bulk-delete`, { data: { leadIds } });
};

/**
 * Assign lead to user
 * @param {string} id - Lead ID
 * @param {string} userId - User ID to assign to
 * @returns {Promise} Updated lead
 */
export const assignLead = (id, userId, assignedToDetails) => {
  return api.patch(`${CRM_BASE_URL}/leads/${id}/assign`, { userId, assignedToDetails });
};

/**
 * Bulk assign leads to user
 * @param {Object} data - Bulk assignment data { leadIds: Array, assignedTo: String }
 * @returns {Promise} Bulk assignment result
 */
/**
 * Bulk assign leads to a user (and move them into a target lead pool)
 * @param {Object} data - { leadIds, targetPoolId, assignedTo, assignedToDetails? }
 * @returns {Promise} Bulk assignment result
 */
export const bulkAssignLeads = (data) => {
  return api.post(`${CRM_BASE_URL}/leads/bulk-assign`, data);
};

/**
 * Add follow-up activity to lead
 * @param {string} id - Lead ID
 * @param {Object} activityData - Activity data (type, subject, description, outcome)
 * @returns {Promise} Updated lead with new activity
 */
export const addLeadActivity = (id, activityData) => {
  return api.post(`${CRM_BASE_URL}/leads/${id}/activity`, activityData);
};

/**
 * Edit an existing lead activity (keeps the linked calendar activity in sync)
 * @param {string} id - Lead ID
 * @param {string} activityId - Embedded activity _id
 * @param {Object} activityData - Activity data (type, subject, description, outcome, scheduledDate, reminderEnabled, reminderMinutes)
 * @returns {Promise} Updated lead
 */
export const editLeadActivity = (id, activityId, activityData) => {
  return api.put(`${CRM_BASE_URL}/leads/${id}/activities/${activityId}`, activityData);
};

/**
 * Validate lead email and NIC uniqueness
 * @param {Object} data - { email, nic, leadId } - leadId is optional for update validation
 * @returns {Promise} Validation result { email: boolean, nic: boolean }
 */
export const validateLeadUniqueness = (data) => {
  return api.post(`${CRM_BASE_URL}/leads/validate`, data);
};

/**
 * Lookup a student by email and/or nationalId (case-insensitive)
 * @param {Object} params - { email?: string, nationalId?: string }
 * @returns {Promise} Student summary or null
 */
export const lookupStudent = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/students/lookup`, { params });
};

// ============================================================================
// LEAD POOL MANAGEMENT
// ============================================================================

/**
 * Get all lead pools with filters
 * @param {Object} params - Query parameters
 * @returns {Promise} Lead pool list
 */
export const getLeadPools = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/lead-pools`, { params });
};

/**
 * Get active lead pools, optionally scoped to a location
 * @param {string} [locationId] - Location ID to filter by
 * @returns {Promise} Active lead pools list
 */
export const getActiveLeadPools = (locationId, options = {}) => {
  const params = {};
  if (locationId) params.location = locationId;
  if (options.include) {
    params.include = Array.isArray(options.include)
      ? options.include.filter(Boolean).join(',')
      : options.include;
  }
  return api.get(`${CRM_BASE_URL}/lead-pools/active`, { params });
};

/**
 * Get lead pools by type
 * @param {string} type - Pool type (campaign, team, region, segment, custom)
 * @returns {Promise} Lead pools list
 */
export const getLeadPoolsByType = (type) => {
  return api.get(`${CRM_BASE_URL}/lead-pools/type/${type}`);
};

/**
 * Get lead pool by ID
 * @param {string} id - Lead pool ID
 * @returns {Promise} Lead pool details
 */
export const getLeadPoolById = (id) => {
  return api.get(`${CRM_BASE_URL}/lead-pools/${id}`);
};

/**
 * Get transferred-out trail for a lead pool (Came from / Sent to / Now in)
 * @param {string} id - Lead pool ID
 * @param {Object} params - page, limit, search, dateFrom, dateTo, nowInPool, sentToPool, transferredBy
 */
export const getLeadPoolTransferredOut = (id, params = {}) => {
  return api.get(`${CRM_BASE_URL}/lead-pools/${id}/transferred-out`, { params });
};

/**
 * Create new lead pool
 * @param {Object} poolData - Lead pool data
 * @returns {Promise} Created lead pool
 */
export const createLeadPool = (poolData) => {
  return api.post(`${CRM_BASE_URL}/lead-pools`, poolData);
};

/**
 * Update lead pool
 * @param {string} id - Lead pool ID
 * @param {Object} poolData - Updated lead pool data
 * @returns {Promise} Updated lead pool
 */
export const updateLeadPool = (id, poolData) => {
  return api.put(`${CRM_BASE_URL}/lead-pools/${id}`, poolData);
};

/**
 * Delete lead pool
 * @param {string} id - Lead pool ID
 * @returns {Promise} Success message
 */
export const deleteLeadPool = (id) => {
  return api.delete(`${CRM_BASE_URL}/lead-pools/${id}`);
};

/**
 * Add leads to pool
 * @param {string} poolId - Lead pool ID
 * @param {Array} leadIds - Array of lead IDs to add
 * @returns {Promise} Updated lead pool
 */
export const addLeadsToPool = (poolId, leadIds) => {
  return api.post(`${CRM_BASE_URL}/lead-pools/${poolId}/leads`, { leadIds });
};

/**
 * Remove leads from pool
 * @param {string} poolId - Lead pool ID
 * @param {Array} leadIds - Array of lead IDs to remove
 * @returns {Promise} Updated lead pool
 */
export const removeLeadsFromPool = (poolId, leadIds) => {
  return api.delete(`${CRM_BASE_URL}/lead-pools/${poolId}/leads`, { data: { leadIds } });
};

/**
 * Remove single lead from pool
 * @param {string} poolId - Lead pool ID
 * @param {string} leadId - Lead ID to remove
 * @returns {Promise} Updated lead pool
 */
export const removeLeadFromPool = (poolId, leadId) => {
  return removeLeadsFromPool(poolId, [leadId]);
};

/**
 * Transfer leads from one pool to another
 * @param {string} sourcePoolId - Source lead pool ID
 * @param {string} targetPoolId - Target lead pool ID
 * @param {Array<string>} leadIds - Array of lead IDs to transfer
 * @param {string} assigneeId - Target pool user to assign transferred leads to
 * @param {Object} [assignedToDetails] - Optional denormalized assignee details
 * @returns {Promise} Transfer result
 */
export const transferLeadsToPool = (sourcePoolId, targetPoolId, leadIds, assigneeId, assignedToDetails) => {
  return api.post(`${CRM_BASE_URL}/lead-pools/${sourcePoolId}/transfer`, {
    targetPoolId,
    leadIds,
    assigneeId,
    ...(assignedToDetails ? { assignedToDetails } : {})
  });
};

/**
 * Sync pool leads - ensures Lead documents have correct leadPool reference
 * @param {string} poolId - Lead pool ID
 * @returns {Promise} Sync result
 */
export const syncPoolLeads = (poolId) => {
  return api.post(`${CRM_BASE_URL}/lead-pools/${poolId}/sync`);
};

/**
 * Get lead pool statistics
 * @param {string} poolId - Lead pool ID
 * @returns {Promise} Pool statistics
 */
export const getLeadPoolStatistics = (poolId) => {
  return api.get(`${CRM_BASE_URL}/lead-pools/${poolId}/statistics`);
};

// ============================================================================
// OPPORTUNITY MANAGEMENT
// ============================================================================

/**
 * Get all opportunities
 * @param {Object} params - Query parameters
 * @returns {Promise} Opportunity list
 */
export const getOpportunities = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/opportunities`, { params });
};

/**
 * Get opportunity by ID
 * @param {string} id - Opportunity ID
 * @returns {Promise} Opportunity details
 */
export const getOpportunityById = (id) => {
  return api.get(`${CRM_BASE_URL}/opportunities/${id}`);
};

/**
 * Get opportunity statistics
 * @returns {Promise} Opportunity statistics
 */
export const getOpportunityStats = () => {
  return api.get(`${CRM_BASE_URL}/opportunities/stats`);
};

/**
 * Create new opportunity
 * @param {Object} opportunityData - Opportunity data
 * @returns {Promise} Created opportunity
 */
export const createOpportunity = (opportunityData) => {
  return api.post(`${CRM_BASE_URL}/opportunities`, opportunityData);
};

/**
 * Update opportunity
 * @param {string} id - Opportunity ID
 * @param {Object} opportunityData - Updated opportunity data
 * @returns {Promise} Updated opportunity
 */
export const updateOpportunity = (id, opportunityData) => {
  return api.put(`${CRM_BASE_URL}/opportunities/${id}`, opportunityData);
};

/**
 * Move opportunity to next stage
 * @param {string} id - Opportunity ID
 * @param {string} stage - New stage
 * @returns {Promise} Updated opportunity
 */
export const moveOpportunityStage = (id, stage) => {
  return api.patch(`${CRM_BASE_URL}/opportunities/${id}/stage`, { stage });
};

/**
 * Close opportunity (won or lost)
 * @param {string} id - Opportunity ID
 * @param {Object} closeData - Close data (status, reason)
 * @returns {Promise} Closed opportunity
 */
export const closeOpportunity = (id, closeData) => {
  return api.post(`${CRM_BASE_URL}/opportunities/${id}/close`, closeData);
};

// ============================================================================
// TRAINING MANAGEMENT
// ============================================================================

/**
 * Get all trainings
 * @param {Object} params - Query parameters
 * @returns {Promise} Training list
 */
export const getTrainings = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/training`, { params });
};

/**
 * Get training by ID
 * @param {string} id - Training ID
 * @returns {Promise} Training details
 */
export const getTrainingById = (id) => {
  return api.get(`${CRM_BASE_URL}/training/${id}`);
};

/**
 * Create training
 * @param {Object} trainingData - Training data
 * @returns {Promise} Created training
 */
export const createTraining = (trainingData) => {
  return api.post(`${CRM_BASE_URL}/training`, trainingData);
};

/**
 * Update training
 * @param {string} id - Training ID
 * @param {Object} trainingData - Updated training data
 * @returns {Promise} Updated training
 */
export const updateTraining = (id, trainingData) => {
  return api.put(`${CRM_BASE_URL}/training/${id}`, trainingData);
};

/**
 * Delete/Archive training
 * @param {string} id - Training ID
 * @returns {Promise} Success message
 */
export const deleteTraining = (id) => {
  return api.delete(`${CRM_BASE_URL}/training/${id}`);
};

/**
 * Get all training products
 * @param {Object} params - Query parameters
 * @returns {Promise} Product list
 */
export const getTrainingProducts = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/training/products`, { params });
};

/**
 * Get training product by ID
 * @param {string} id - Product ID
 * @returns {Promise} Product details
 */
export const getTrainingProductById = (id) => {
  return api.get(`${CRM_BASE_URL}/training/products/${id}`);
};

/**
 * Create training product
 * @param {Object} productData - Product data
 * @returns {Promise} Created product
 */
export const createTrainingProduct = (productData) => {
  return api.post(`${CRM_BASE_URL}/training/products`, productData);
};

/**
 * Get training sessions
 * @param {Object} params - Query parameters
 * @returns {Promise} Session list
 */
export const getTrainingSessions = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/training/sessions`, { params });
};

/**
 * Register participant for training
 * @param {string} sessionId - Session ID
 * @param {Object} participantData - Participant data
 * @returns {Promise} Registration confirmation
 */
export const registerTrainingParticipant = (sessionId, participantData) => {
  return api.post(`${CRM_BASE_URL}/training/sessions/${sessionId}/register`, participantData);
};

/**
 * Register for training (alias for registerTrainingParticipant)
 * @param {string} sessionId - Session ID
 * @param {Object} participantData - Participant data
 * @returns {Promise} Registration confirmation
 */
export const registerForTraining = (sessionId, participantData) => {
  return api.post(`${CRM_BASE_URL}/training/sessions/${sessionId}/register`, participantData);
};

/**
 * Mark training attendance
 * @param {string} participantId - Participant ID
 * @returns {Promise} Attendance confirmation
 */
export const markTrainingAttendance = (participantId) => {
  return api.post(`${CRM_BASE_URL}/training/participants/${participantId}/attend`);
};

/**
 * Cancel training registration
 * @param {string} participantId - Participant ID
 * @returns {Promise} Cancellation confirmation
 */
export const cancelTrainingRegistration = (participantId) => {
  return api.post(`${CRM_BASE_URL}/training/participants/${participantId}/cancel`);
};

/**
 * Generate training certificate
 * @param {string} participantId - Participant ID
 * @returns {Promise} Certificate PDF
 */
export const generateTrainingCertificate = (participantId) => {
  return api.post(`${CRM_BASE_URL}/training/participants/${participantId}/certificate`, {}, {
    responseType: 'blob'
  });
};

// ============================================================================
// WORKSHOP MANAGEMENT
// ============================================================================

/**
 * Get all workshops
 * @param {Object} params - Query parameters
 * @returns {Promise} Workshop list
 */
export const getWorkshops = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/workshops`, { params });
};

/**
 * Get workshop by ID
 * @param {string} id - Workshop ID
 * @returns {Promise} Workshop details
 */
export const getWorkshopById = (id) => {
  return api.get(`${CRM_BASE_URL}/workshops/${id}`);
};

/**
 * Create workshop
 * @param {Object} workshopData - Workshop data
 * @returns {Promise} Created workshop
 */
export const createWorkshop = (workshopData) => {
  return api.post(`${CRM_BASE_URL}/workshops`, workshopData);
};

/**
 * Update workshop
 * @param {string} id - Workshop ID
 * @param {Object} workshopData - Updated workshop data
 * @returns {Promise} Updated workshop
 */
export const updateWorkshop = (id, workshopData) => {
  return api.put(`${CRM_BASE_URL}/workshops/${id}`, workshopData);
};

/**
 * Delete/Archive workshop
 * @param {string} id - Workshop ID
 * @returns {Promise} Success message
 */
export const deleteWorkshop = (id) => {
  return api.delete(`${CRM_BASE_URL}/workshops/${id}`);
};

/**
 * Register for workshop
 * @param {string} workshopId - Workshop ID
 * @param {Object} registrationData - Registration data
 * @returns {Promise} Registration confirmation
 */
export const registerForWorkshop = (workshopId, registrationData) => {
  return api.post(`${CRM_BASE_URL}/workshops/${workshopId}/register`, registrationData);
};

/**
 * Check-in workshop participant
 * @param {string} registrationId - Registration ID
 * @returns {Promise} Check-in confirmation
 */
export const workshopCheckIn = (registrationId) => {
  return api.post(`${CRM_BASE_URL}/workshops/registrations/${registrationId}/checkin`);
};

/**
 * Cancel workshop registration
 * @param {string} registrationId - Registration ID
 * @returns {Promise} Cancellation confirmation
 */
export const cancelWorkshopRegistration = (registrationId) => {
  return api.post(`${CRM_BASE_URL}/workshops/registrations/${registrationId}/cancel`);
};

/**
 * Get workshop registration by QR code
 * @param {string} qrCode - QR code
 * @returns {Promise} Registration details
 */
export const getWorkshopRegistrationByQR = (qrCode) => {
  return api.get(`${CRM_BASE_URL}/workshops/registrations/qr/${qrCode}`);
};

/**
 * Generate workshop QR code
 * @param {string} workshopId - Workshop ID
 * @returns {Promise} QR code image
 */
export const generateWorkshopQRCode = (workshopId) => {
  return api.get(`${CRM_BASE_URL}/workshops/${workshopId}/qrcode`, {
    responseType: 'blob'
  });
};

/**
 * Convert workshop attendees to leads
 * @param {Object} data - Conversion data (workshopId, registrationIds, poolId, newPool)
 * @returns {Promise} Conversion result
 */
export const convertWorkshopAttendeesToLeads = (data) => {
  return api.post(`${CRM_BASE_URL}/workshops/convert-attendees-to-leads`, data);
};

/**
 * Generate payment link for workshop registration
 * @param {string} registrationId - Workshop registration ID
 * @param {Object} data - Payment link options
 * @returns {Promise} Payment link details
 */
export const generateWorkshopPaymentLink = (registrationId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/workshops/registrations/${registrationId}/generate-payment-link`, data);
};

/**
 * Verify workshop payment
 * @param {string} registrationId - Workshop registration ID
 * @param {Object} verificationData - Payment verification data (paymentStatus, notes)
 * @returns {Promise} Payment verification result
 */
export const verifyWorkshopPayment = (registrationId, verificationData) => {
  return api.post(`${CRM_BASE_URL}/workshops/registrations/${registrationId}/verify-payment`, verificationData);
};

// ============================================================================
// REFUND MANAGEMENT
// ============================================================================

/**
 * Get all refunds with pagination and filters
 * @param {Object} params - Query parameters (page, limit, search, status, type, studentId, enrollmentId)
 * @returns {Promise} Refund list response
 */
export const getRefunds = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/refunds`, { params });
};

/**
 * Get all refunds (alias for getRefunds)
 * @param {Object} params - Query parameters
 * @returns {Promise} Refund list response
 */
export const getAllRefunds = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/refunds`, { params });
};

/**
 * Get refund by ID
 * @param {string} id - Refund ID
 * @returns {Promise} Refund details
 */
export const getRefundById = (id) => {
  return api.get(`${CRM_BASE_URL}/refunds/${id}`);
};

/**
 * Create new refund
 * @param {Object} refundData - Refund data
 * @returns {Promise} Created refund
 */
export const createRefund = (refundData) => {
  return api.post(`${CRM_BASE_URL}/refunds`, refundData);
};

/**
 * Update refund
 * @param {string} id - Refund ID
 * @param {Object} refundData - Updated refund data
 * @returns {Promise} Updated refund
 */
export const updateRefund = (id, refundData) => {
  return api.put(`${CRM_BASE_URL}/refunds/${id}`, refundData);
};

/**
 * Delete refund (soft delete - only pending refunds)
 * @param {string} id - Refund ID
 * @returns {Promise} Success message
 */
export const deleteRefund = (id) => {
  return api.delete(`${CRM_BASE_URL}/refunds/${id}`);
};

/**
 * Approve refund
 * @param {string} id - Refund ID
 * @param {Object} data - Approval data (notes)
 * @returns {Promise} Approved refund
 */
export const approveRefund = (id, data = {}) => {
  return api.post(`${CRM_BASE_URL}/refunds/${id}/approve`, data);
};

/**
 * Reject refund
 * @param {string} id - Refund ID
 * @param {Object} data - Rejection data (reason)
 * @returns {Promise} Rejected refund
 */
export const rejectRefund = (id, data) => {
  return api.post(`${CRM_BASE_URL}/refunds/${id}/reject`, data);
};

/**
 * Process refund (mark as processed/completed)
 * @param {string} id - Refund ID
 * @param {Object} data - Processing data (notes, transactionReference)
 * @returns {Promise} Processed refund
 */
export const processRefund = (id, data = {}) => {
  return api.post(`${CRM_BASE_URL}/refunds/${id}/process`, data);
};

/**
 * Cancel refund
 * @param {string} id - Refund ID
 * @param {Object} data - Cancellation data (reason)
 * @returns {Promise} Cancelled refund
 */
export const cancelRefund = (id, data) => {
  return api.post(`${CRM_BASE_URL}/refunds/${id}/cancel`, data);
};

/**
 * Get refunds by student
 * @param {string} studentId - Student ID
 * @param {Object} params - Query parameters
 * @returns {Promise} Refund list
 */
export const getRefundsByStudent = (studentId, params = {}) => {
  return api.get(`${CRM_BASE_URL}/refunds/student/${studentId}`, { params });
};

/**
 * Get refunds by enrollment
 * @param {string} enrollmentId - Enrollment ID
 * @param {Object} params - Query parameters
 * @returns {Promise} Refund list
 */
export const getRefundsByEnrollment = (enrollmentId, params = {}) => {
  return api.get(`${CRM_BASE_URL}/refunds/enrollment/${enrollmentId}`, { params });
};

/**
 * Get refund statistics
 * @param {Object} params - Query parameters (dateFrom, dateTo, locationId)
 * @returns {Promise} Statistics data
 */
export const getRefundStatistics = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/refunds/statistics`, { params });
};

// ============================================================================
// CREDIT NOTE MANAGEMENT
// ============================================================================

/**
 * Get all credit notes with pagination and filters
 */
export const getCreditNotes = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/credit-notes`, { params });
};

/**
 * Get credit note by ID
 */
export const getCreditNoteById = (id) => {
  return api.get(`${CRM_BASE_URL}/credit-notes/${id}`);
};

/**
 * Create new credit note
 */
export const createCreditNote = (data) => {
  return api.post(`${CRM_BASE_URL}/credit-notes`, data);
};

/**
 * Update credit note (only pending)
 */
export const updateCreditNote = (id, data) => {
  return api.put(`${CRM_BASE_URL}/credit-notes/${id}`, data);
};

/**
 * Delete credit note (only pending)
 */
export const deleteCreditNote = (id) => {
  return api.delete(`${CRM_BASE_URL}/credit-notes/${id}`);
};

/**
 * Approve credit note (posts GL double-entry)
 */
export const approveCreditNote = (id, data = {}) => {
  return api.post(`${CRM_BASE_URL}/credit-notes/${id}/approve`, data);
};

/**
 * Reject credit note
 */
export const rejectCreditNote = (id, data) => {
  return api.post(`${CRM_BASE_URL}/credit-notes/${id}/reject`, data);
};

/**
 * Cancel credit note
 */
export const cancelCreditNote = (id, data) => {
  return api.post(`${CRM_BASE_URL}/credit-notes/${id}/cancel`, data);
};

/**
 * Get credit notes by student
 */
export const getCreditNotesByStudent = (studentId, params = {}) => {
  return api.get(`${CRM_BASE_URL}/credit-notes/student/${studentId}`, { params });
};

/**
 * Get a student's payments (to select which payment a credit note is raised against)
 */
export const getStudentPaymentsForCreditNote = (studentId) => {
  return api.get(`${CRM_BASE_URL}/credit-notes/student/${studentId}/payments`);
};

/**
 * Get credit note statistics
 */
export const getCreditNoteStatistics = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/credit-notes/statistics`, { params });
};

/**
 * Bulk-create credit notes
 * @param {Object} data - { items: [{ studentId, applicationPaymentId, amount, type, reason, ... }] }
 */
export const createBulkCreditNotes = (data) => {
  return api.post(`${CRM_BASE_URL}/credit-notes/bulk`, data);
};

/**
 * Write off small invoice balance (<= 10 LKR or custom threshold)
 * @param {Object} data - { invoiceId, moduleSource, threshold, reason }
 */
export const writeOffCreditNoteBalance = (data) => {
  return api.post(`${CRM_BASE_URL}/credit-notes/write-off-balance`, data);
};

/**
 * Get credit notes by original invoice ID
 * @param {string} invoiceId
 */
export const getCreditNotesByInvoice = (invoiceId) => {
  return api.get(`${CRM_BASE_URL}/credit-notes/invoice/${invoiceId}`);
};

/**
 * Allocate / apply an approved on-account credit note toward a specific invoice
 * @param {string} id - Credit Note ID
 * @param {Object} data - { applicationPaymentId, amount, notes }
 */
export const allocateCreditNote = (id, data) => {
  return api.post(`${CRM_BASE_URL}/credit-notes/${id}/allocate`, data);
};


/**
 * List sub-products with report target summaries
 */
export const getReportTargets = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/report-targets`, { params });
};

/**
 * Get report targets for a sub-product
 */
export const getReportTargetsBySubProduct = (subProductId) => {
  return api.get(`${CRM_BASE_URL}/report-targets/${subProductId}`);
};

/**
 * Replace report targets for a sub-product
 */
export const updateReportTargets = (subProductId, reportTargets) => {
  return api.put(`${CRM_BASE_URL}/report-targets/${subProductId}`, { reportTargets });
};

// ============================================================================
// QUOTATION MANAGEMENT
// ============================================================================

/**
 * Get all quotations
 * @param {Object} params - Query parameters
 * @returns {Promise} Quotation list
 */
export const getQuotations = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/quotations`, { params });
};

/**
 * Get quotation by ID
 * @param {string} id - Quotation ID
 * @returns {Promise} Quotation details
 */
export const getQuotationById = (id) => {
  return api.get(`${CRM_BASE_URL}/quotations/${id}`);
};

/**
 * Create quotation
 * @param {Object} quotationData - Quotation data
 * @returns {Promise} Created quotation
 */
export const createQuotation = (quotationData) => {
  return api.post(`${CRM_BASE_URL}/quotations`, quotationData);
};

/**
 * Send quotation to customer
 * @param {string} id - Quotation ID
 * @returns {Promise} Send confirmation
 */
export const sendQuotation = (id) => {
  return api.post(`${CRM_BASE_URL}/quotations/${id}/send`);
};

/**
 * Convert quotation to order
 * @param {string} id - Quotation ID
 * @returns {Promise} Created order
 */
export const convertQuotationToOrder = (id) => {
  return api.post(`${CRM_BASE_URL}/quotations/${id}/convert`);
};

/**
 * Generate quotation PDF
 * @param {string} id - Quotation ID
 * @returns {Promise} PDF file
 */
export const generateQuotationPDF = (id) => {
  return api.get(`${CRM_BASE_URL}/quotations/${id}/pdf`, {
    responseType: 'blob'
  });
};

// ============================================================================
// CAMPAIGN MANAGEMENT
// ============================================================================

/**
 * Get all campaigns
 * @param {Object} params - Query parameters
 * @returns {Promise} Campaign list
 */
export const getCampaigns = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/campaigns`, { params });
};

/**
 * Get campaign by ID
 * @param {string} id - Campaign ID
 * @returns {Promise} Campaign details
 */
export const getCampaignById = (id) => {
  return api.get(`${CRM_BASE_URL}/campaigns/${id}`);
};

/**
 * Create campaign
 * @param {Object} campaignData - Campaign data
 * @returns {Promise} Created campaign
 */
export const createCampaign = (campaignData) => {
  return api.post(`${CRM_BASE_URL}/campaigns`, campaignData);
};

/**
 * Launch campaign
 * @param {string} id - Campaign ID
 * @returns {Promise} Launch confirmation
 */
export const launchCampaign = (id) => {
  return api.post(`${CRM_BASE_URL}/campaigns/${id}/launch`);
};

/**
 * Get campaign analytics
 * @param {string} id - Campaign ID
 * @returns {Promise} Campaign analytics
 */
export const getCampaignAnalytics = (id) => {
  return api.get(`${CRM_BASE_URL}/campaigns/${id}/analytics`);
};

// ============================================================================
// SUPPORT TICKET MANAGEMENT
// ============================================================================

/**
 * Get all support tickets
 * @param {Object} params - Query parameters
 * @returns {Promise} Ticket list
 */
export const getSupportTickets = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/support/tickets`, { params });
};

/**
 * Get ticket by ID
 * @param {string} id - Ticket ID
 * @returns {Promise} Ticket details
 */
export const getSupportTicketById = (id) => {
  return api.get(`${CRM_BASE_URL}/support/tickets/${id}`);
};

/**
 * Create support ticket
 * @param {Object} ticketData - Ticket data
 * @returns {Promise} Created ticket
 */
export const createSupportTicket = (ticketData) => {
  return api.post(`${CRM_BASE_URL}/support/tickets`, ticketData);
};

/**
 * Add comment to ticket
 * @param {string} ticketId - Ticket ID
 * @param {Object} commentData - Comment data
 * @returns {Promise} Added comment
 */
export const addTicketComment = (ticketId, commentData) => {
  return api.post(`${CRM_BASE_URL}/support/tickets/${ticketId}/comments`, commentData);
};

/**
 * Update ticket status
 * @param {string} id - Ticket ID
 * @param {string} status - New status
 * @returns {Promise} Updated ticket
 */
export const updateTicketStatus = (id, status) => {
  return api.patch(`${CRM_BASE_URL}/support/tickets/${id}/status`, { status });
};

// ============================================================================
// CONTRACT MANAGEMENT
// ============================================================================

/**
 * Get all contracts
 * @param {Object} params - Query parameters
 * @returns {Promise} Contract list
 */
export const getContracts = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/contracts`, { params });
};

/**
 * Get contract by ID
 * @param {string} id - Contract ID
 * @returns {Promise} Contract details
 */
export const getContractById = (id) => {
  return api.get(`${CRM_BASE_URL}/contracts/${id}`);
};

/**
 * Create contract
 * @param {Object} contractData - Contract data
 * @returns {Promise} Created contract
 */
export const createContract = (contractData) => {
  return api.post(`${CRM_BASE_URL}/contracts`, contractData);
};

/**
 * Send contract for signature
 * @param {string} id - Contract ID
 * @returns {Promise} Send confirmation
 */
export const sendContractForSignature = (id) => {
  return api.post(`${CRM_BASE_URL}/contracts/${id}/send`);
};

/**
 * Renew contract
 * @param {string} id - Contract ID
 * @param {Object} renewalData - Renewal data
 * @returns {Promise} Renewed contract
 */
export const renewContract = (id, renewalData) => {
  return api.post(`${CRM_BASE_URL}/contracts/${id}/renew`, renewalData);
};

// ============================================================================
// ANALYTICS & REPORTING
// ============================================================================

/**
 * Get CRM dashboard data
 * @returns {Promise} Dashboard data
 */
export const getCRMDashboard = () => {
  return api.get(`${CRM_BASE_URL}/analytics/dashboard`);
};

/**
 * Get lead statistics (status breakdown, source breakdown, recent leads, etc.)
 * @returns {Promise} Lead stats
 */
export const getLeadStats = () => {
  return api.get(`${CRM_BASE_URL}/leads/stats`);
};

/**
 * Get sales pipeline analytics
 * @param {Object} params - Query parameters
 * @returns {Promise} Pipeline analytics
 */
export const getSalesPipeline = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/analytics/pipeline`, { params });
};

/**
 * Get customer insights
 * @param {Object} params - Query parameters
 * @returns {Promise} Customer insights
 */
export const getCustomerInsights = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/analytics/customers`, { params });
};

/**
 * Get revenue forecast
 * @param {Object} params - Query parameters
 * @returns {Promise} Revenue forecast
 */
export const getRevenueForecast = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/analytics/forecast`, { params });
};

/**
 * Export CRM report
 * @param {string} reportType - Report type
 * @param {Object} params - Report parameters
 * @returns {Promise} Report file
 */
export const exportCRMReport = (reportType, params = {}) => {
  return api.get(`${CRM_BASE_URL}/analytics/reports/${reportType}/export`, {
    params,
    responseType: 'blob'
  });
};

// ============================================================================
// EDUCATIONAL PRODUCT MANAGEMENT
// ============================================================================

/**
 * Get all product categories
 * @param {Object} params - Query parameters
 * @returns {Promise} Category list
 */
export const getCategories = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/categories`, { params });
};

/**
 * Get category by ID
 * @param {string} id - Category ID
 * @returns {Promise} Category details
 */
export const getCategoryById = (id) => {
  return api.get(`${CRM_BASE_URL}/categories/${id}`);
};

/**
 * Create product category
 * @param {Object} categoryData - Category data
 * @returns {Promise} Created category
 */
export const createCategory = (categoryData) => {
  return api.post(`${CRM_BASE_URL}/categories`, categoryData);
};

/**
 * Update category
 * @param {string} id - Category ID
 * @param {Object} categoryData - Updated category data
 * @returns {Promise} Updated category
 */
export const updateCategory = (id, categoryData) => {
  return api.put(`${CRM_BASE_URL}/categories/${id}`, categoryData);
};

/**
 * Delete/Archive category
 * @param {string} id - Category ID
 * @returns {Promise} Success message
 */
export const deleteCategory = (id) => {
  return api.delete(`${CRM_BASE_URL}/categories/${id}`);
};

/**
 * Get all main products
 * @param {Object} params - Query parameters
 * @returns {Promise} Product list
 */
export const getProducts = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/products`, { params });
};

/**
 * Get product by ID
 * @param {string} id - Product ID
 * @returns {Promise} Product details
 */
export const getProductById = (id) => {
  return api.get(`${CRM_BASE_URL}/products/${id}`);
};

/**
 * Create main product
 * @param {Object} productData - Product data
 * @returns {Promise} Created product
 */
export const createProduct = (productData) => {
  return api.post(`${CRM_BASE_URL}/products`, productData);
};

/**
 * Update product
 * @param {string} id - Product ID
 * @param {Object} productData - Updated product data
 * @returns {Promise} Updated product
 */
export const updateProduct = (id, productData) => {
  return api.put(`${CRM_BASE_URL}/products/${id}`, productData);
};

/**
 * Delete/Archive product
 * @param {string} id - Product ID
 * @returns {Promise} Success message
 */
export const deleteProduct = (id) => {
  return api.delete(`${CRM_BASE_URL}/products/${id}`);
};

/**
 * Get all sub-products
 * @param {Object} params - Query parameters
 * @returns {Promise} Sub-product list
 */
export const getSubProducts = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/sub-products`, { params });
};

/**
 * Get sub-product by ID
 * @param {string} id - Sub-product ID
 * @returns {Promise} Sub-product details
 */
export const getSubProductById = (id) => {
  return api.get(`${CRM_BASE_URL}/sub-products/${id}`);
};

/**
 * Create sub-product
 * @param {Object} subProductData - Sub-product data
 * @returns {Promise} Created sub-product
 */
export const createSubProduct = (subProductData) => {
  return api.post(`${CRM_BASE_URL}/sub-products`, subProductData);
};

/**
 * Update sub-product
 * @param {string} id - Sub-product ID
 * @param {Object} subProductData - Updated sub-product data
 * @returns {Promise} Updated sub-product
 */
export const updateSubProduct = (id, subProductData) => {
  return api.put(`${CRM_BASE_URL}/sub-products/${id}`, subProductData);
};

/**
 * Delete/Archive sub-product
 * @param {string} id - Sub-product ID
 * @returns {Promise} Success message
 */
export const deleteSubProduct = (id) => {
  return api.delete(`${CRM_BASE_URL}/sub-products/${id}`);
};

/**
 * Get sub-product schedule
 * @param {string} id - Sub-product ID
 * @returns {Promise} Schedule details
 */
export const getSubProductSchedule = (id) => {
  return api.get(`${CRM_BASE_URL}/sub-products/${id}/schedule`);
};

/**
 * Calculate sub-product pricing
 * @param {string} id - Sub-product ID
 * @param {Object} params - Pricing parameters (discountCode, studentCount)
 * @returns {Promise} Pricing details
 */
export const calculateSubProductPricing = (id, params = {}) => {
  return api.get(`${CRM_BASE_URL}/sub-products/${id}/pricing`, { params });
};

/**
 * Generate special discount codes (unique sequence)
 * @param {number} count - Number of codes to generate
 * @returns {Promise} Codes list
 */
export const generateSpecialDiscountCodes = (count, type = 'special_discount') => {
  return api.post(`${CRM_BASE_URL}/discount-codes/generate`, { count, type });
};

/**
 * Get sub-product analytics
 * @param {string} id - Sub-product ID
 * @returns {Promise} Analytics data
 */
export const getSubProductAnalytics = (id) => {
  return api.get(`${CRM_BASE_URL}/sub-products/${id}/analytics`);
};

/**
 * Search sub-products
 * @param {Object} params - Search parameters
 * @returns {Promise} Search results
 */
export const searchSubProducts = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/sub-products/search`, { params });
};

// ============================================================================
// INTAKE MANAGEMENT
// ============================================================================

/**
 * Get all intakes
 * @param {Object} params - Query parameters
 * @returns {Promise} Intake list
 */
export const getIntakes = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/intakes`, { params });
};

/**
 * Get intake by ID
 * @param {string} id - Intake ID
 * @returns {Promise} Intake details
 */
export const getIntakeById = (id) => {
  return api.get(`${CRM_BASE_URL}/intakes/${id}`);
};

/**
 * Create intake
 * @param {Object} intakeData - Intake data
 * @returns {Promise} Created intake
 */
export const createIntake = (intakeData) => {
  return api.post(`${CRM_BASE_URL}/intakes`, intakeData);
};

/**
 * Update intake
 * @param {string} id - Intake ID
 * @param {Object} intakeData - Updated intake data
 * @returns {Promise} Updated intake
 */
export const updateIntake = (id, intakeData) => {
  return api.put(`${CRM_BASE_URL}/intakes/${id}`, intakeData);
};

/**
 * Delete/Archive intake
 * @param {string} id - Intake ID
 * @returns {Promise} Success message
 */
export const deleteIntake = (id) => {
  return api.delete(`${CRM_BASE_URL}/intakes/${id}`);
};

/**
 * Get intakes by sub-product
 * @param {string} subProductId - Sub-product ID
 * @returns {Promise} Intake list
 */
export const getIntakesBySubProduct = (subProductId) => {
  return api.get(`${CRM_BASE_URL}/intakes/sub-product/${subProductId}`);
};

/**
 * Get intakes by main product
 * @param {string} mainProductId - Main product ID
 * @returns {Promise} Intake list
 */
export const getIntakesByMainProduct = (mainProductId) => {
  return api.get(`${CRM_BASE_URL}/intakes/main-product/${mainProductId}`);
};

/**
 * Calculate intake pricing
 * @param {string} id - Intake ID
 * @param {Object} params - Pricing parameters (discountCode, studentCount)
 * @returns {Promise} Pricing details
 */
export const calculateIntakePricing = (id, params = {}) => {
  return api.get(`${CRM_BASE_URL}/intakes/${id}/calculate-price`, { params });
};

/**
 * Update intake status
 * @param {string} id - Intake ID
 * @param {string} status - New status
 * @returns {Promise} Updated intake
 */
export const updateIntakeStatus = (id, status) => {
  return api.patch(`${CRM_BASE_URL}/intakes/${id}/status`, { status });
};

/**
 * Get intake statistics
 * @param {string} id - Intake ID
 * @returns {Promise} Statistics data
 */
export const getIntakeStatistics = (id) => {
  return api.get(`${CRM_BASE_URL}/intakes/${id}/statistics`);
};

/**
 * Add enrollment to intake
 * @param {string} id - Intake ID
 * @param {Object} data - Enrollment data
 * @returns {Promise} Success message
 */
export const addIntakeEnrollment = (id, data) => {
  return api.post(`${CRM_BASE_URL}/intakes/${id}/enrollment`, data);
};

/**
 * Remove enrollment from intake
 * @param {string} id - Intake ID
 * @param {Object} data - Enrollment data
 * @returns {Promise} Success message
 */
export const removeIntakeEnrollment = (id, data) => {
  return api.delete(`${CRM_BASE_URL}/intakes/${id}/enrollment`, { data });
};

// ============================================================================
// ENROLLMENT MANAGEMENT
// ============================================================================

/**
 * Get all enrollments
 * @param {Object} params - Query parameters
 * @returns {Promise} Enrollment list
 */
export const getEnrollments = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/enrollments`, { params });
};

/**
 * Get enrollment by ID
 * @param {string} id - Enrollment ID
 * @returns {Promise} Enrollment details
 */
export const getEnrollmentById = (id) => {
  return api.get(`${CRM_BASE_URL}/enrollments/${id}`);
};

/**
 * Search candidate students and leads for enrollment creation
 * @param {Object} params - Query params (search, limit)
 * @returns {Promise} Matching candidate students, leads, and submitted applications
 */
export const searchEnrollmentCandidates = (params) => {
  return api.get(`${CRM_BASE_URL}/enrollments/candidate-search`, { params });
};

/**
 * Create enrollment
 * @param {Object} enrollmentData - Enrollment data
 * @returns {Promise} Created enrollment
 */
export const createEnrollment = (enrollmentData) => {
  return api.post(`${CRM_BASE_URL}/enrollments`, enrollmentData);
};

/**
 * Update enrollment
 * @param {string} id - Enrollment ID
 * @param {Object} enrollmentData - Updated enrollment data
 * @returns {Promise} Updated enrollment
 */
export const updateEnrollment = (id, enrollmentData) => {
  return api.put(`${CRM_BASE_URL}/enrollments/${id}`, enrollmentData);
};

/**
 * Delete enrollment
 * @param {string} id - Enrollment ID
 * @returns {Promise} Success message
 */
export const deleteEnrollment = (id) => {
  return api.delete(`${CRM_BASE_URL}/enrollments/${id}`);
};

/**
 * Approve enrollment
 * @param {string} id - Enrollment ID
 * @returns {Promise} Updated enrollment
 */
export const approveEnrollment = (id) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/approve`);
};

/**
 * Reject direct enrollment
 * @param {string} id - Enrollment ID
 * @param {Object} data - { rejectionReason, paymentAction }
 * @returns {Promise} Updated enrollment
 */
export const rejectEnrollment = (id, data) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/reject`, data);
};

/**
 * Confirm enrollment
 * @param {string} id - Enrollment ID
 * @returns {Promise} Updated enrollment
 */
export const confirmEnrollment = (id) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/confirm`);
};

/**
 * Record payment for enrollment
 * @param {string} id - Enrollment ID
 * @param {Object} paymentData - Payment details
 * @returns {Promise} Updated enrollment
 */
export const recordEnrollmentPayment = (id, paymentData) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/payment`, paymentData);
};

/**
 * Generate a signed public payment link for an enrollment installment or repeat charge
 * @param {string} id - Enrollment ID
 * @param {Object} data - Link target details
 * @returns {Promise} Payment link response
 */
export const generateEnrollmentPaymentLink = (id, data = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/generate-payment-link`, data);
};

/**
 * Generate / resend required-document submission link for a direct enrollment
 */
export const generateEnrollmentDocumentLink = (id, data = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/generate-document-link`, data);
};

/**
 * Get enrollment by document submission token (public)
 */
export const getEnrollmentByDocumentToken = (token) => {
  return api.get(`${CRM_BASE_URL}/enrollments/public/documents/${token}`);
};

/**
 * Delete a previously uploaded enrollment required document (public)
 */
export const deletePublicEnrollmentDocument = (token, fileId) => {
  return api.delete(`${CRM_BASE_URL}/enrollments/public/documents/${token}/files/${fileId}`);
};

/**
 * Approve an enrollment required document
 */
export const approveEnrollmentDocument = (enrollmentId, fileId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${enrollmentId}/documents/${fileId}/approve`, data);
};

/**
 * Reject an enrollment required document
 */
export const rejectEnrollmentDocument = (enrollmentId, fileId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${enrollmentId}/documents/${fileId}/reject`, data);
};

/**
 * Approve or reject the scholarship proof of a direct enrollment ({ action: 'approve' | 'reject', note })
 */
export const reviewEnrollmentScholarshipProof = (enrollmentId, data) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${enrollmentId}/scholarship-proof/review`, data);
};

/**
 * Add a Pending payment for a direct (non-application) enrollment — cashier Manual Payments
 */
export const addManualEnrollmentPayment = (id, paymentData) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/manual-payment`, paymentData);
};

/**
 * Approve a Pending direct-enrollment payment
 */
export const approveManualEnrollmentPayment = (id, paymentId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/payments/${paymentId}/approve`, data);
};

/** Digital Sales Receipt PDF (T&W format). Pass variant: 'preprinted' for continuous stationery overlay. */
export const printEnrollmentPaymentReceipt = (id, paymentId, { variant } = {}) => {
  return api.get(`${CRM_BASE_URL}/enrollments/${id}/payments/${paymentId}/receipt/print`, {
    responseType: 'blob',
    params: variant ? { variant } : undefined,
  });
};

/** Send / Resend digital Sales Receipt (PDF Email & SMS) to the student. */
export const emailEnrollmentPaymentReceipt = (id, paymentId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/payments/${paymentId}/receipt/email`, data);
};
export const resendEnrollmentPaymentReceipt = emailEnrollmentPaymentReceipt;

/** Resend Enrollment Confirmation (Email & SMS) */
export const resendEnrollmentConfirmation = (id) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/resend-confirmation`);
};

/**
 * Approved credit notes available for a direct enrollment's student
 */
export const getAvailableCreditNotesForEnrollment = (id) => {
  return api.get(`${CRM_BASE_URL}/enrollments/${id}/available-credit-notes`);
};

/**
 * Reject a Pending direct-enrollment payment
 */
export const rejectManualEnrollmentPayment = (id, paymentId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/payments/${paymentId}/reject`, data);
};

/**
 * Reverse an Approved direct-enrollment payment
 */
export const reverseManualEnrollmentPayment = (id, paymentId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/payments/${paymentId}/reverse`, data);
};

export const reopenRejectedManualEnrollmentPayment = (id, paymentId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/payments/${paymentId}/reopen-rejected`, data);
};

/**
 * List repeat charges for an enrollment
 * @param {string} id - Enrollment ID
 * @returns {Promise} Repeat charge list
 */
export const getEnrollmentRepeatCharges = (id) => {
  return api.get(`${CRM_BASE_URL}/enrollments/${id}/repeat-charges`);
};

/**
 * Create an exam repeat charge for an enrollment
 * @param {string} id - Enrollment ID
 * @param {Object} repeatChargeData - Exam repeat charge payload
 * @returns {Promise} Created repeat charge
 */
export const createEnrollmentExamRepeatCharge = (id, repeatChargeData) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/repeat-charges/exam-resit`, repeatChargeData);
};

/**
 * Create a module repeat charge for an enrollment
 * @param {string} id - Enrollment ID
 * @param {Object} repeatChargeData - Module repeat charge payload
 * @returns {Promise} Created repeat charge
 */
export const createEnrollmentModuleRepeatCharge = (id, repeatChargeData) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/repeat-charges/module-repeat`, repeatChargeData);
};

/**
 * Batch create, approve, and send module repeat payment links
 */
export const batchSendEnrollmentModuleRepeatCharges = (id, data) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/repeat-charges/module-repeat/batch-send`, data);
};

/**
 * Create a module repeat charge that moves the module(s) to another intake
 * @param {string} id - Enrollment ID
 * @param {Object} data - { moduleCodes, targetSubProductId, collectionMode, notes }
 * @returns {Promise} Created repeat charge
 */
export const createEnrollmentModuleMoveCharge = (id, data) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/repeat-charges/module-move`, data);
};

/**
 * Batch create, approve, and send exam resit payment links
 */
export const batchSendEnrollmentExamResitCharges = (id, data) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/repeat-charges/exam-resit/batch-send`, data);
};

/**
 * Create a full intake repeat charge for an enrollment
 * @param {string} id - Enrollment ID
 * @param {Object} repeatChargeData - Intake repeat charge payload
 * @returns {Promise} Created repeat charge
 */
export const createEnrollmentIntakeRepeatCharge = (id, repeatChargeData) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/repeat-charges/intake-repeat`, repeatChargeData);
};

/**
 * Approve an enrollment repeat charge
 * @param {string} id - Enrollment ID
 * @param {string} chargeId - Repeat charge ID
 * @param {Object} data - Optional approval notes
 * @returns {Promise} Approved repeat charge
 */
export const approveEnrollmentRepeatCharge = (id, chargeId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/repeat-charges/${chargeId}/approve`, data);
};
/**
 * Cancel an enrollment repeat charge and reverse posted finance entries
 * @param {string} id - Enrollment ID
 * @param {string} chargeId - Repeat charge ID
 * @param {Object} data - Cancellation reason and optional notes
 * @returns {Promise} Updated repeat charge
 */
export const cancelEnrollmentRepeatCharge = (id, chargeId, data) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/repeat-charges/${chargeId}/cancel`, data);
};

/**
 * Record payment for an enrollment repeat charge
 * @param {string} id - Enrollment ID
 * @param {string} chargeId - Repeat charge ID
 * @param {Object} paymentData - Payment details
 * @returns {Promise} Updated repeat charge
 */
export const recordEnrollmentRepeatChargePayment = (id, chargeId, paymentData) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/repeat-charges/${chargeId}/payment`, paymentData);
};
/**
 * Reverse an approved repeat charge payment
 * @param {string} id - Enrollment ID
 * @param {string} chargeId - Repeat charge ID
 * @param {string} paymentId - Repeat charge payment ID
 * @param {Object} data - Reversal reason and optional notes
 * @returns {Promise} Updated repeat charge
 */
export const reverseEnrollmentRepeatChargePayment = (id, chargeId, paymentId, data) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/repeat-charges/${chargeId}/payments/${paymentId}/reverse`, data);
};

/**
 * Approve a pending repeat charge payment (staff)
 */
export const approveEnrollmentRepeatChargePayment = (id, chargeId, paymentId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/repeat-charges/${chargeId}/payments/${paymentId}/approve`, data);
};

/**
 * Reject a pending repeat charge payment (staff)
 */
export const rejectEnrollmentRepeatChargePayment = (id, chargeId, paymentId, data) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/repeat-charges/${chargeId}/payments/${paymentId}/reject`, data);
};

/**
 * Initiate Paycorp card payment for a repeat charge (public)
 */
export const initiateEnrollmentRepeatChargeCardPayment = (token, data = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/public/${token}/initiate-card`, data);
};

/**
 * Create the successor enrollment for an approved intake repeat charge
 * @param {string} id - Enrollment ID
 * @param {string} chargeId - Repeat charge ID
 * @param {Object} data - Optional payload
 * @returns {Promise} Created enrollment and updated repeat charge
 */
export const createEnrollmentRepeatEnrollment = (id, chargeId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/repeat-charges/${chargeId}/create-repeat-enrollment`, data);
};

/**
 * Record attendance for enrollment
 * @param {string} id - Enrollment ID
 * @param {Object} attendanceData - Attendance details
 * @returns {Promise} Updated enrollment
 */
export const recordEnrollmentAttendance = (id, attendanceData) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/attendance`, attendanceData);
};

/**
 * Update attendance record for enrollment
 * @param {string} id - Enrollment ID
 * @param {string} recordId - Attendance record ID
 * @param {Object} attendanceData - Attendance details
 * @returns {Promise} Updated enrollment
 */
export const updateEnrollmentAttendance = (id, recordId, attendanceData) => {
  return api.put(`${CRM_BASE_URL}/enrollments/${id}/attendance/${recordId}`, attendanceData);
};

/**
 * Delete attendance record for enrollment
 * @param {string} id - Enrollment ID
 * @param {string} recordId - Attendance record ID
 * @returns {Promise} Updated enrollment
 */
export const deleteEnrollmentAttendance = (id, recordId) => {
  return api.delete(`${CRM_BASE_URL}/enrollments/${id}/attendance/${recordId}`);
};

/**
 * Submit feedback for enrollment
 * @param {string} id - Enrollment ID
 * @param {Object} feedbackData - Feedback details
 * @returns {Promise} Updated enrollment
 */
export const submitEnrollmentFeedback = (id, feedbackData) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/feedback`, feedbackData);
};

/**
 * Withdraw enrollment
 * @param {string} id - Enrollment ID
 * @param {Object} withdrawalData - Withdrawal details
 * @returns {Promise} Updated enrollment
 */
export const withdrawEnrollment = (id, withdrawalData) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/withdraw`, withdrawalData);
};

/**
 * Defer enrollment to a different intake
 * @param {string} id - Enrollment ID
 * @param {Object} deferralData - { targetIntakeId, reason }
 * @returns {Promise} Updated enrollment
 */
export const deferEnrollment = (id, deferralData) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/defer`, deferralData);
};

/**
 * Issue certificate for enrollment
 * @param {string} id - Enrollment ID
 * @param {Object} certificateData - Certificate details
 * @returns {Promise} Updated enrollment
 */
export const issueEnrollmentCertificate = (id, certificateData) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/certificate`, certificateData);
};

/**
 * Get enrollments by student
 * @param {string} studentId - Student ID
 * @returns {Promise} Enrollment list
 */
export const getEnrollmentsByStudent = (studentId) => {
  return api.get(`${CRM_BASE_URL}/enrollments/student/${studentId}`);
};

/**
 * Get enrollments by intake
 * @param {string} intakeId - Intake ID
 * @returns {Promise} Enrollment list
 */
export const getEnrollmentsByIntake = (intakeId) => {
  return api.get(`${CRM_BASE_URL}/enrollments/intake/${intakeId}`);
};

/**
 * Get enrollment statistics
 * @returns {Promise} Statistics data
 */
export const getEnrollmentStatistics = () => {
  return api.get(`${CRM_BASE_URL}/enrollments/statistics`);
};

// ============================================================================
// GRADUATION MANAGEMENT
// ============================================================================

/**
 * Get all graduations
 * @param {Object} params - Query parameters
 * @returns {Promise} Graduation list
 */
export const getGraduations = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/graduations`, { params });
};

/**
 * Get graduation by ID
 * @param {string} id - Graduation ID
 * @returns {Promise} Graduation details
 */
export const getGraduationById = (id) => {
  return api.get(`${CRM_BASE_URL}/graduations/${id}`);
};

/**
 * Create graduation
 * @param {Object} graduationData - Graduation data
 * @returns {Promise} Created graduation
 */
export const createGraduation = (graduationData) => {
  return api.post(`${CRM_BASE_URL}/graduations`, graduationData);
};

/**
 * Update graduation
 * @param {string} id - Graduation ID
 * @param {Object} graduationData - Updated graduation data
 * @returns {Promise} Updated graduation
 */
export const updateGraduation = (id, graduationData) => {
  return api.put(`${CRM_BASE_URL}/graduations/${id}`, graduationData);
};

/**
 * Delete graduation
 * @param {string} id - Graduation ID
 * @returns {Promise} Success message
 */
export const deleteGraduation = (id) => {
  return api.delete(`${CRM_BASE_URL}/graduations/${id}`);
};

/**
 * Restore graduation
 * @param {string} id - Graduation ID
 * @returns {Promise} Restored graduation
 */
export const restoreGraduation = (id) => {
  return api.post(`${CRM_BASE_URL}/graduations/${id}/restore`);
};

/**
 * Get eligible students for graduation
 * @param {Object} params - Query parameters (subProductIds, intakeIds)
 * @returns {Promise} Eligible students list
 */
export const getEligibleStudents = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/graduations/eligible-students`, { params });
};

/**
 * Add students to graduation
 * @param {string} id - Graduation ID
 * @param {Array} students - Students to add
 * @returns {Promise} Updated graduation
 */
export const addStudentsToGraduation = (id, students) => {
  return api.post(`${CRM_BASE_URL}/graduations/${id}/students`, { students });
};

/**
 * Remove student from graduation
 * @param {string} id - Graduation ID
 * @param {string} studentId - Student ID
 * @param {string} enrollmentId - Enrollment ID
 * @returns {Promise} Updated graduation
 */
export const removeStudentFromGraduation = (id, studentId, enrollmentId) => {
  return api.delete(`${CRM_BASE_URL}/graduations/${id}/students`, {
    data: { studentId, enrollmentId }
  });
};

/**
 * Confirm student attendance
 * @param {string} id - Graduation ID
 * @param {string} studentId - Student ID
 * @param {string} enrollmentId - Enrollment ID
 * @param {number} guestCount - Number of guests
 * @returns {Promise} Updated graduation
 */
export const confirmStudentAttendance = (id, studentId, enrollmentId, guestCount) => {
  return api.post(`${CRM_BASE_URL}/graduations/${id}/students/confirm-attendance`, {
    studentId,
    enrollmentId,
    guestCount
  });
};

/**
 * Issue certificate to student
 * @param {string} id - Graduation ID
 * @param {string} studentId - Student ID
 * @param {string} enrollmentId - Enrollment ID
 * @param {string} certificateNumber - Certificate number
 * @param {string} certificateUrl - Certificate URL
 * @returns {Promise} Updated graduation
 */
export const issueCertificateToStudent = (id, studentId, enrollmentId, certificateNumber, certificateUrl) => {
  return api.post(`${CRM_BASE_URL}/graduations/${id}/students/issue-certificate`, {
    studentId,
    enrollmentId,
    certificateNumber,
    certificateUrl
  });
};

/**
 * Update graduation status
 * @param {string} id - Graduation ID
 * @param {string} status - New status
 * @returns {Promise} Updated graduation
 */
export const updateGraduationStatus = (id, status) => {
  return api.patch(`${CRM_BASE_URL}/graduations/${id}/status`, { status });
};

/**
 * Get graduation statistics
 * @param {string} id - Graduation ID
 * @returns {Promise} Statistics data
 */
export const getGraduationStatistics = (id) => {
  return api.get(`${CRM_BASE_URL}/graduations/${id}/statistics`);
};

/**
 * Get upcoming graduations
 * @param {Object} params - Query parameters
 * @returns {Promise} Upcoming graduations
 */
export const getUpcomingGraduations = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/graduations/upcoming`, { params });
};

/**
 * Get past graduations
 * @param {Object} params - Query parameters
 * @returns {Promise} Past graduations
 */
export const getPastGraduations = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/graduations/past`, { params });
};

export const approveGraduation = (id) => {
  return api.post(`${CRM_BASE_URL}/graduations/${id}/approve`);
};

export const markStudentAbsent = (id, studentId, enrollmentId) => {
  return api.post(`${CRM_BASE_URL}/graduations/${id}/students/absent`, { studentId, enrollmentId });
};

export const batchUpdateAttendance = (id, attendances) => {
  return api.post(`${CRM_BASE_URL}/graduations/${id}/attendance/bulk-update`, { attendances });
};

export const refundNonAttendeeOverride = (id, studentId, enrollmentId, reason) => {
  return api.post(`${CRM_BASE_URL}/graduations/${id}/students/refund-override`, { studentId, enrollmentId, reason });
};

export const generateGraduationDepositLinks = (id, studentIds, options = {}) => {
  return api.post(`${CRM_BASE_URL}/graduations/${id}/deposit/generate-links`, { studentIds, ...options });
};

export const resendGraduationDepositLink = (id, studentId, enrollmentId, forceRegenerate = false) => {
  return api.post(`${CRM_BASE_URL}/graduations/${id}/deposit/resend-link`, { studentId, enrollmentId, forceRegenerate });
};

export const getGraduationDepositPayments = (id, params = {}) => {
  return api.get(`${CRM_BASE_URL}/graduations/${id}/deposit/payments`, { params });
};

export const getGraduationDepositPaymentById = (paymentId) => {
  return api.get(`${CRM_BASE_URL}/graduations/deposit/payments/${paymentId}`);
};

export const approveGraduationDepositPayment = (paymentId, actualAmount, notes, financeFields = {}) => {
  return api.post(`${CRM_BASE_URL}/graduations/deposit/payments/${paymentId}/approve`, {
    actualAmount,
    notes,
    ...financeFields
  });
};

/** Digital Sales Receipt PDF (T&W format). Pass variant: 'preprinted' for continuous stationery overlay. */
export const printGraduationDepositReceipt = (paymentId, { variant } = {}) => {
  return api.get(`${CRM_BASE_URL}/graduations/deposit/payments/${paymentId}/receipt/print`, {
    responseType: 'blob',
    params: variant ? { variant } : undefined,
  });
};

/** Email digital Sales Receipt PDF to the student (or override email). */
export const emailGraduationDepositReceipt = (paymentId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/graduations/deposit/payments/${paymentId}/receipt/email`, data);
};

export const rejectGraduationDepositPayment = (paymentId, reason) => {
  return api.post(`${CRM_BASE_URL}/graduations/deposit/payments/${paymentId}/reject`, { reason });
};

export const reverseGraduationDepositPayment = (paymentId, reason, notes) => {
  return api.post(`${CRM_BASE_URL}/graduations/deposit/payments/${paymentId}/reverse`, { reason, notes });
};

export const getGraduationDepositSummary = (id) => {
  return api.get(`${CRM_BASE_URL}/graduations/${id}/deposit/summary`);
};

export const waiveGraduationDeposit = (id, studentId, enrollmentId, reason) => {
  return api.post(`${CRM_BASE_URL}/graduations/${id}/deposit/waive`, { studentId, enrollmentId, reason });
};

export const addStaffManualGraduationDepositPayment = (id, paymentData) => {
  return api.post(`${CRM_BASE_URL}/graduations/${id}/deposit/manual-payment`, paymentData);
};

export const getPublicGraduationDepositDetails = (token) => {
  return api.get(`${PUBLIC_BASE_URL}/graduations/deposit/${token}`);
};

export const submitPublicGraduationDepositPayment = (token, paymentData) => {
  return api.post(`${PUBLIC_BASE_URL}/graduations/deposit/${token}`, paymentData);
};

export const initiatePublicGraduationDepositCardPayment = (token) => {
  return api.post(`${PUBLIC_BASE_URL}/graduations/deposit/${token}/initiate-card`);
};

// ============================================================================
// STUDENT MANAGEMENT
// ============================================================================

/**
 * Get all students with pagination and filters
 * @param {Object} params - Query parameters (page, limit, sort, status, search, assignedTo)
 * @returns {Promise} Students list response
 */
export const getAllStudents = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/students`, { params });
};

/**
 * Get student by ID with enrollments
 * @param {string} id - Student ID
 * @returns {Promise} Student details with enrollments
 */
export const getStudentById = (id) => {
  return api.get(`${CRM_BASE_URL}/students/${id}`);
};

/**
 * Get student by student number
 * @param {string} studentNumber - Student number (e.g., STU202400001)
 * @returns {Promise} Student details
 */
export const getStudentByNumber = (studentNumber) => {
  return api.get(`${CRM_BASE_URL}/students/number/${studentNumber}`);
};

/**
 * Get all prospective students
 * @returns {Promise} Prospective students list
 */
export const getProspectiveStudents = () => {
  return api.get(`${CRM_BASE_URL}/students/prospective`);
};

/**
 * Get all active students
 * @returns {Promise} Active students list
 */
export const getActiveStudents = () => {
  return api.get(`${CRM_BASE_URL}/students/active`);
};

/**
 * Create new student
 * @param {Object} studentData - Student data
 * @returns {Promise} Created student
 */
export const createStudent = (studentData) => {
  return api.post(`${CRM_BASE_URL}/students`, studentData);
};

/**
 * Update student
 * @param {string} id - Student ID
 * @param {Object} studentData - Updated student data
 * @returns {Promise} Updated student
 */
export const updateStudent = (id, studentData) => {
  return api.put(`${CRM_BASE_URL}/students/${id}`, studentData);
};

/**
 * Validate whether student unique fields already exist
 * @param {Object} params - { email?, nationalId?, excludeId? }
 * @returns {Promise} { success, data: { emailExists, nationalIdExists } }
 */
export const validateStudentUniqueFields = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/students/validate-unique`, { params });
};

/**
 * Update student status
 * @param {string} id - Student ID
 * @param {string} status - New status (prospective, active, graduated, withdrawn, deferred, suspended, alumni)
 * @returns {Promise} Updated student
 */
export const updateStudentStatus = (id, status) => {
  return api.patch(`${CRM_BASE_URL}/students/${id}/status`, { status });
};

/**
 * Verify student documents
 * @param {string} id - Student ID
 * @param {Object} data - Document verification details (documentType, verifiedDate)
 * @returns {Promise} Updated student
 */
export const verifyStudentDocuments = (id, data) => {
  return api.patch(`${CRM_BASE_URL}/students/${id}/verify-documents`, data);
};

/**
 * Get student enrollments
 * @param {string} id - Student ID
 * @returns {Promise} Student enrollments list
 */
export const getStudentEnrollments = (id) => {
  return api.get(`${CRM_BASE_URL}/students/${id}/enrollments`);
};

/**
 * Delete student
 * @param {string} id - Student ID
 * @returns {Promise} Success message
 */
export const deleteStudent = (id) => {
  return api.delete(`${CRM_BASE_URL}/students/${id}`);
};

// Application Functions
/**
 * Create a new application
 * @param {Object} applicationData - Application data
 * @returns {Promise} Application object
 */
export const createApplication = (applicationData) => {
  return api.post(`${CRM_BASE_URL}/applications`, applicationData);
};

/**
 * Get the membership fee apply / do-not-apply option (and whether it can still be edited).
 */
export const getApplicationMembershipFeeOption = (applicationId) => {
  return api.get(`${CRM_BASE_URL}/applications/${applicationId}/membership-fee-option`);
};

/**
 * Change the membership fee option; rejected by the server once a payment exists.
 */
export const updateApplicationMembershipFeeOption = (applicationId, applyMembershipFee) => {
  return api.patch(`${CRM_BASE_URL}/applications/${applicationId}/membership-fee-option`, { applyMembershipFee });
};

/**
 * Update assignee-locked module selections on a draft application.
 * @param {string} applicationId
 * @param {Array} moduleSelections - array of module codes or selection objects
 */
export const updateApplicationModuleSelections = (applicationId, moduleSelections, modulePricingTerms) => {
  const body = Array.isArray(moduleSelections)
    ? {
      moduleSelections,
      ...(modulePricingTerms ? { modulePricingTerms } : {})
    }
    : moduleSelections;

  return api.patch(`${CRM_BASE_URL}/applications/${applicationId}/module-selections`, body);
};

/**
 * Update discount eligibility on a draft unpaid application.
 * @param {string} applicationId
 * @param {{ allowFullPayment: boolean, allowInstallmentPlans: boolean }} discountEligibility
 */
export const updateApplicationDiscountEligibility = (applicationId, discountEligibility) => {
  return api.patch(`${CRM_BASE_URL}/applications/${applicationId}/discount-eligibility`, {
    discountEligibility
  });
};

/**
 * Get existing application (and public application link) for lead + program
 * in the public application workflow.
 * @param {string} leadId - Lead ID
 * @param {Object} params - { mainProductId, subProductId, intakeId? }
 */
export const getApplicationForLeadWorkflow = (leadId, params = {}) => {
  return api.get(`${CRM_BASE_URL}/applications/lead-workflow/by-lead/${leadId}`, {
    params
  });
};

/**
 * Get application by ID
 * @param {string} id - Application ID
 * @returns {Promise} Application object
 */
export const getApplicationById = (id) => {
  return api.get(`${CRM_BASE_URL}/applications/${id}`);
};

/**
 * Get application by token
 * @param {string} token - Application token
 * @returns {Promise} Application object
 */
export const getApplicationByToken = (token) => {
  return api.get(`${CRM_BASE_URL}/applications/token/${token}`, { skipErrorToast: true });
};

/**
 * Get application by application number or ID (public access)
 * @param {string} applicationNumberOrId - Application number or MongoDB ID
 * @returns {Promise} Application object
 */
/**
 * Get application by document submission token
 * @param {string} token - Document submission token
 * @returns {Promise} Application data
 */
export const getApplicationByDocumentToken = (token) => {
  return api.get(`${CRM_BASE_URL}/applications/public/documents/${token}`, { skipErrorToast: true });
};

/**
 * Delete a previously uploaded application document (public, by document token)
 * @param {string} token - Document submission token
 * @param {string} fileId - File ID to remove
 */
export const deletePublicApplicationDocument = (token, fileId) => {
  return api.delete(`${CRM_BASE_URL}/applications/public/documents/${token}/files/${fileId}`, { skipErrorToast: true });
};

/**
 * Get application by application number or ID (public)
 * @param {string} applicationNumberOrId - Application number or ID
 * @returns {Promise} Application data
 */
export const getApplicationByNumber = (applicationNumberOrId) => {
  return api.get(`${CRM_BASE_URL}/applications/public/${applicationNumberOrId}`, { skipErrorToast: true });
};

/**
 * Verify sub-product finance mappings and CRM payment settings before sending an application link.
 * @param {string} leadId - Lead ID
 * @returns {Promise} Readiness result
 */
export const getLeadApplicationLinkReadiness = (leadId) => {
  return api.get(`${CRM_BASE_URL}/leads/${leadId}/application-link-readiness`);
};

/**
 * Send public application link email to a lead
 * @param {string} leadId - Lead ID
 * @param {Object} payload - { applicationId?: string, applicationLink?: string }
 * @returns {Promise} Email send result
 */
export const sendLeadApplicationLinkEmail = (leadId, payload) => {
  return api.post(`${CRM_BASE_URL}/leads/${leadId}/send-application-link`, payload);
};

/**
 * Get student list report data with course details
 * @param {Object} params - Filters: search, status, gender, source, mainProductId, subProductId, intakeId, dateFrom, dateTo, page, limit
 * @returns {Promise} Paginated report data
 */
export const getStudentReport = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/students`, { params });
};

/**
 * Export student list report as CSV (all records)
 * @param {Object} params - Same filters as getStudentReport, format is auto-set to csv
 * @returns {Promise<Blob>} CSV file blob
 */
export const exportStudentReportCsv = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/students`, {
    params: { ...params, format: 'csv' },
    responseType: 'blob',
    timeout: 600000,
    validateStatus: (s) => s < 500,
    timeout: 120000,
  });
};

/**
 * Download student list report as a server-generated PDF.
 * The backend auto-selects A4 portrait / A4 landscape / A3 landscape
 * based on the number of requested columns.
 * @param {Object} params - Same filters as getStudentReport; columns param is honoured
 * @returns {Promise<Blob>} PDF file blob
 */
export const downloadStudentReportPdf = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/students`, {
    params: { ...params, format: 'pdf' },
    responseType: 'blob',
    validateStatus: (s) => s < 500,
    timeout: 120000,
  });
};

/**
 * Export Eligibility Questions report as multi-sheet XLSX
 * @param {Object} params - { subProductId, locationId, intakeId, status, startDate, endDate }
 * @returns {Promise} Blob response
 */
export const exportEligibilityQuestionsReport = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/eligibility-questions/export`, {
    params,
    responseType: 'blob',
    validateStatus: (s) => s < 500,
    timeout: 180000,
  });
};

/**
 * Get Eligibility Questions report metrics summary
 * @param {Object} params - { subProductId, locationId, intakeId, status, startDate, endDate }
 * @returns {Promise}
 */
export const getEligibilityQuestionsSummary = (params = {}, config = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/eligibility-questions/summary`, { params, ...config });
};

/**
 * Module Enrollment Matrix (students x modules) for a module-based sub-product
 * @param {Object} params - { subProductId, intakeId, locationIds, stages, moduleCodes, search }
 */
export const getModuleEnrollmentMatrix = (params = {}, config = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/module-enrollment-matrix`, { params, ...config });
};

export const getModuleEnrollmentMatrixOptions = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/module-enrollment-matrix/options`, { params });
};

export const exportModuleEnrollmentMatrix = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/module-enrollment-matrix/export`, {
    params,
    responseType: 'blob',
    validateStatus: (s) => s < 500,
    timeout: 180000,
  });
};

export const getLeadReport = (params = {}, config = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/leads`, { params, ...config });
};

export const exportLeadReportCsv = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/leads`, {
    params: { ...params, format: 'csv' },
    responseType: 'blob',
    validateStatus: (s) => s < 500,
    // Large filtered sets stream in batches; allow more time than the default 30s.
    timeout: 120000,
  });
};

export const downloadLeadReportPdf = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/leads`, {
    params: { ...params, format: 'pdf' },
    responseType: 'blob',
    validateStatus: (s) => s < 500,
    timeout: 120000,
  });
};

export const getStudentCreditReport = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/student-credits`, { params });
};

export const exportStudentCreditReportCsv = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/student-credits`, {
    params: { ...params, format: 'csv' },
    responseType: 'blob',
    validateStatus: (s) => s < 500
  });
};

export const downloadStudentCreditReportPdf = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/student-credits`, {
    params: { ...params, format: 'pdf' },
    responseType: 'blob',
    validateStatus: (s) => s < 500
  });
};

export const getPaymentReport = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/payments`, { params });
};

export const exportPaymentReportCsv = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/payments`, {
    params: { ...params, format: 'csv' },
    responseType: 'blob',
    validateStatus: (s) => s < 500,
    timeout: 120000,
  });
};

export const downloadPaymentReportPdf = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/payments`, {
    params: { ...params, format: 'pdf' },
    responseType: 'blob',
    validateStatus: (s) => s < 500,
    timeout: 120000,
  });
};

export const getArReconciliation = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/ar-reconciliation`, { params });
};

export const getFailedGlPostings = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/gl-failed-postings`, { params });
};

export const getStudentAccountStatement = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/student-account-statement`, { params });
};

export const exportStudentAccountStatementCsv = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/student-account-statement`, {
    params: { ...params, format: 'csv' },
    responseType: 'blob',
    validateStatus: (s) => s < 500,
    timeout: 120000,
  });
};

export const downloadStudentAccountStatementPdf = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/student-account-statement`, {
    params: { ...params, format: 'pdf' },
    responseType: 'blob',
    validateStatus: (s) => s < 500,
    timeout: 120000,
  });
};

export const triggerGlRetry = (body = {}) => {
  return api.post(`${CRM_BASE_URL}/reports/gl-retry`, body);
};

export const getExtendedCRMReport = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/extended`, { params, timeout: 120000 });
};

export const exportExtendedCRMReportCsv = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/extended`, {
    params: { ...params, format: 'csv' },
    responseType: 'blob',
    validateStatus: (s) => s < 500,
  });
};

export const exportExtendedCRMReportXlsx = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/extended`, {
    params: { ...params, format: 'xlsx' },
    responseType: 'blob',
    timeout: 600000,
    validateStatus: (s) => s < 500,
  });
};

export const getExportTemplates = () => {
  return api.get(`${CRM_BASE_URL}/reports/export-templates`);
};

export const previewDynamicExport = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/dynamic-export/preview`, { params });
};

export const downloadDynamicExport = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/dynamic-export`, {
    params,
    responseType: 'blob',
    validateStatus: (s) => s < 500,
  });
};

export const downloadAllProductsExport = (params = {}) => downloadDynamicExport({
  ...params,
  template: params.template || 'lead-inquiry-tracking',
});

/**
 * Get all applications with filters
 * @param {Object} filters - Filter parameters
 * @returns {Promise} Applications list
 */
export const getApplications = (filters = {}) => {
  return api.get(`${CRM_BASE_URL}/applications`, { params: filters });
};

/**
 * Sub-status tab counts for application management (decoupled from paginated list).
 */
export const getApplicationSubStatusCounts = (filters = {}) => {
  return api.get(`${CRM_BASE_URL}/applications/sub-status-counts`, { params: filters });
};

/**
 * Update application
 * @param {string} id - Application ID
 * @param {Object} updateData - Update data
 * @returns {Promise} Updated application
 */
export const updateApplication = (id, updateData) => {
  return api.patch(`${CRM_BASE_URL}/applications/${id}`, updateData);
};

/**
 * Update application personal + contact identity fields
 * Requires CRM:APPLICATIONS:UPDATE_IDENTITY
 * @param {string} id - Application ID
 * @param {Object} identityData - { personalInfo, contactInfo }
 * @returns {Promise} Updated application
 */
export const updateApplicationIdentity = (id, identityData) => {
  return api.patch(`${CRM_BASE_URL}/applications/${id}/identity`, identityData);
};

/**
 * Change an application's requirement category (staff, before approval).
 * Marks documents of the previous category rejected. Requires CRM:APPLICATION_DOCUMENTS:CHANGE_CATEGORY
 * @param {string} id - Application ID
 * @param {{requirementsCategoryId: string, reason: string}} payload
 */
export const changeApplicationRequirementCategory = (id, payload) => {
  return api.patch(`${CRM_BASE_URL}/applications/${id}/requirements-category`, payload);
};

/**
 * Change a direct enrollment's requirement category (staff, Pending enrollments only).
 * Requires CRM:ENROLLMENTS:CHANGE_CATEGORY
 * @param {string} id - Enrollment ID
 * @param {{requirementsCategoryId: string, reason: string}} payload
 */
export const changeEnrollmentRequirementCategory = (id, payload) => {
  return api.patch(`${CRM_BASE_URL}/enrollments/${id}/requirements-category`, payload);
};

/**
 * Submit application
 * @param {string} id - Application ID
 * @returns {Promise} Success message
 */
export const submitApplication = (id) => {
  return api.post(`${CRM_BASE_URL}/applications/${id}/submit`);
};

/**
 * Update application by number or ID (public access)
 * @param {string} applicationNumberOrId - Application number or MongoDB ID
 * @param {Object} updateData - Update data
 * @returns {Promise} Updated application
 */
export const updateApplicationByNumber = (applicationNumberOrId, updateData) => {
  return api.put(`${CRM_BASE_URL}/applications/public/${applicationNumberOrId}/update`, updateData);
};

/**
 * Submit application by number or ID (public access)
 * @param {string} applicationNumberOrId - Application number or MongoDB ID
 * @param {Object} data - Additional data including turnstileToken
 * @returns {Promise} Success message
 */
export const submitApplicationByNumber = (applicationNumberOrId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/applications/public/${applicationNumberOrId}/submit`, data);
};

/**
 * Verify eligibility for application
 * @param {string} id - Application ID
 * @param {Object} data - Verification data with notes
 * @returns {Promise} Success message
 */
export const verifyEligibility = (id, data) => {
  return api.post(`${CRM_BASE_URL}/applications/${id}/verify-eligibility`, data);
};

/**
 * Approve application
 * @param {string} id - Application ID
 * @param {Object} data - Approval data with notes
 * @returns {Promise} Success message
 */
export const approveApplication = (id, data) => {
  return api.post(`${CRM_BASE_URL}/applications/${id}/approve`, data);
};

/**
 * Reject application
 * @param {string} id - Application ID
 * @param {Object} data - { rejectionReason, paymentAction }
 *   paymentAction: 'reverse_and_reject' | 'keep_and_reject'
 * @returns {Promise} Success message
 */
export const rejectApplication = (id, data) => {
  return api.post(`${CRM_BASE_URL}/applications/${id}/reject`, data);
};

/**
 * Load lifecycle context for cancel / payment restructure actions
 * @param {string} id - Application ID
 * @returns {Promise}
 */
export const getApplicationLifecycleContext = (id) => {
  return api.get(`${CRM_BASE_URL}/applications/${id}/lifecycle/context`);
};

/**
 * Cancel application via lifecycle API (isolated from legacy reject)
 * @param {string} id - Application ID
 * @param {Object} data - Cancel options
 * @returns {Promise}
 */
export const cancelApplicationLifecycle = (id, data) => {
  return api.post(`${CRM_BASE_URL}/applications/${id}/lifecycle/cancel`, data);
};

/**
 * Restructure application payment plan via lifecycle API
 * @param {string} id - Application ID
 * @param {Object} data - Restructure options
 * @returns {Promise}
 */
export const restructureApplicationPaymentLifecycle = (id, data) => {
  return api.post(`${CRM_BASE_URL}/applications/${id}/lifecycle/restructure-payment`, data);
};

/**
 * Remove application and reset linked lead (hard delete — no finance reversals)
 * @param {string} id - Application ID
 * @param {Object} data - Purge options
 * @returns {Promise}
 */
export const purgeApplicationLifecycle = (id, data) => {
  return api.post(`${CRM_BASE_URL}/applications/${id}/lifecycle/purge`, data);
};

/**
 * Delete application (only draft status)
 * @param {string} id - Application ID
 * @returns {Promise} Success message
 */
export const deleteApplication = (id) => {
  return api.delete(`${CRM_BASE_URL}/applications/${id}`);
};

/**
 * Get eligibility by application
 * @param {string} applicationId - Application ID
 * @returns {Promise} Eligibility data
 */
export const getEligibilityByApplication = (applicationId) => {
  return api.get(`${CRM_BASE_URL}/eligibility/application/${applicationId}`);
};

// ============================================================================
// APPLICATION PAYMENT MANAGEMENT
// ============================================================================

/**
 * Get application payment by application ID
 * @param {string} applicationId - Application ID
 * @returns {Promise} Application payment data
 */
export const getApplicationPaymentByApplication = (applicationId) => {
  return api.get(`${CRM_BASE_URL}/application-payments/application/${applicationId}`);
};

/**
 * Get public application payment by application ID (for public application form)
 * @param {string} applicationId - Application ID
 * @returns {Promise} Application payment data (limited fields)
 */
export const getPublicApplicationPaymentByApplication = (applicationId) => {
  return api.get(`${CRM_BASE_URL}/application-payments/public/by-application/${applicationId}`, { skipErrorToast: true });
};

/**
 * List all application payments
 * @param {Object} params - Query parameters (status, paymentType, page, limit)
 * @returns {Promise} Application payments list
 */
export const listApplicationPayments = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/application-payments`, { params });
};

/**
 * Approve application payment (creates student record and PaymentVerification)
 * @param {string} applicationPaymentId - Application payment ID
 * @param {Object} data - Approval data with optional notes
 * @returns {Promise} Success response with student and payment verification
 */
export const approveApplicationPayment = (applicationPaymentId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/application-payments/${applicationPaymentId}/approve`, data);
};

/**
 * Reject application payment
 * @param {string} applicationPaymentId - Application payment ID
 * @param {Object} data - Rejection data with reason
 * @returns {Promise} Success response
 */
export const rejectApplicationPayment = (applicationPaymentId, data) => {
  return api.post(`${CRM_BASE_URL}/application-payments/${applicationPaymentId}/reject`, data);
};

/**
 * Approve partial payment
 * @param {string} applicationPaymentId - Application payment ID
 * @param {string} partialPaymentId - Partial payment ID
 * @param {Object} data - Approval data with optional notes
 * @returns {Promise} Success response
 */
export const reverseApplicationPayment = (applicationPaymentId, partialPaymentId, data) => {
  return api.post(`${CRM_BASE_URL}/application-payments/${applicationPaymentId}/partial-payments/${partialPaymentId}/reverse`, data);
};

export const reopenRejectedApplicationPayment = (applicationPaymentId, partialPaymentId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/application-payments/${applicationPaymentId}/partial-payments/${partialPaymentId}/reopen-rejected`, data);
};

export const approvePartialApplicationPayment = (applicationPaymentId, partialPaymentId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/application-payments/${applicationPaymentId}/partial-payments/${partialPaymentId}/approve`, data);
};

export const checkPartialPaymentDuplicate = (applicationPaymentId, partialPaymentId) => {
  return api.get(`${CRM_BASE_URL}/application-payments/${applicationPaymentId}/partial-payments/${partialPaymentId}/duplicate-check`);
};

/** Digital Sales Receipt PDF (T&W format). Pass variant: 'preprinted' for continuous stationery overlay. */
export const printApplicationPaymentReceipt = (applicationPaymentId, partialPaymentId, { variant } = {}) => {
  return api.get(
    `${CRM_BASE_URL}/application-payments/${applicationPaymentId}/partial-payments/${partialPaymentId}/receipt/print`,
    {
      responseType: 'blob',
      params: variant ? { variant } : undefined,
    }
  );
};

/** Email digital Sales Receipt PDF to the applicant (or override email). */
export const emailApplicationPaymentReceipt = (applicationPaymentId, partialPaymentId, data = {}) => {
  return api.post(
    `${CRM_BASE_URL}/application-payments/${applicationPaymentId}/partial-payments/${partialPaymentId}/receipt/email`,
    data
  );
};

/**
 * Get approved credit notes available to apply on an application payment
 * @param {string} applicationPaymentId - Application payment ID
 * @returns {Promise} Available credit notes
 */
export const getAvailableCreditNotesForApplicationPayment = (applicationPaymentId) => {
  return api.get(`${CRM_BASE_URL}/application-payments/${applicationPaymentId}/available-credit-notes`);
};

/**
 * Reject partial payment
 * @param {string} applicationPaymentId - Application payment ID
 * @param {string} partialPaymentId - Partial payment ID
 * @param {Object} data - Rejection data with reason
 * @returns {Promise} Success response
 */
export const rejectPartialApplicationPayment = (applicationPaymentId, partialPaymentId, data) => {
  return api.post(`${CRM_BASE_URL}/application-payments/${applicationPaymentId}/partial-payments/${partialPaymentId}/reject`, data);
};

/**
 * Create ApplicationPayment for skipped payments
 * @param {Object} data - Data with applicationId, moduleSelections (for module-based), paymentType, installmentPlanId (for product-based)
 * @returns {Promise} Success response
 */
export const createApplicationPaymentForSkipped = (data) => {
  return api.post(`${CRM_BASE_URL}/application-payments/create-for-skipped`, data);
};

/**
 * Generate payment link for ApplicationPayment
 * @param {string} applicationPaymentId - Application payment ID
 * @returns {Promise} Success response with payment link
 */
export const generateApplicationPaymentLink = (applicationPaymentId) => {
  return api.post(`${CRM_BASE_URL}/application-payments/${applicationPaymentId}/generate-payment-link`);
};

/**
 * Bulk-generate payment links for multiple applications.
 * Responds immediately; link generation + email/SMS runs in the background.
 * @param {string[]} applicationIds - Array of Application._id values (max 100)
 * @returns {Promise} { accepted: N } — number of IDs accepted for processing
 */
export const bulkGenerateApplicationPaymentLinks = (applicationIds) => {
  return api.post(`${CRM_BASE_URL}/application-payments/bulk-generate-payment-links`, { applicationIds });
};

/**
 * Bulk extend document submission links for applications
 * Extends link validity by 30 days and dispatches renewal notifications via email and SMS.
 * @param {string[]} applicationIds - Array of Application._id values (max 100)
 * @returns {Promise} Result of bulk extension
 */
export const bulkExtendApplicationDocumentLinks = (applicationIds) => {
  return api.post(`${CRM_BASE_URL}/applications/bulk-extend-document-links`, { applicationIds });
};

/**
 * Send payment link email for ApplicationPayment
 * @param {string} applicationPaymentId - Application payment ID
 * @returns {Promise} Email send result
 */
export const sendApplicationPaymentLinkEmail = (applicationPaymentId) => {
  return api.post(`${CRM_BASE_URL}/application-payments/${applicationPaymentId}/send-payment-link-email`);
};

/**
 * Add a manual (staff-entered) payment to an ApplicationPayment
 * The payment lands as status=Pending and must be approved/rejected in the Payment & Enrollment panel.
 * Permission: CRM:MANUAL_PAYMENTS:ADD
 *
 * Allowed when:
 *   - Application has NOT been approved yet (pre-approval partial payment), OR
 *   - Application is Approved AND an enrollment was created from it (post-approval installment)
 *
 * @param {string} applicationPaymentId - ApplicationPayment _id
 * @param {Object} paymentData
 * @param {number}  paymentData.amount           - Payment amount (required)
 * @param {string}  paymentData.paymentMethod    - e.g. 'Cash', 'Bank Transfer', 'Cheque' (required)
 * @param {string}  [paymentData.referenceNumber] - Receipt / reference number
 * @param {string}  [paymentData.paymentDate]     - ISO date string (defaults to today)
 * @param {string}  [paymentData.notes]           - Staff notes
 * @param {number}  [paymentData.installmentNumber] - Target installment (auto-detected if omitted)
 * @returns {Promise} Updated ApplicationPayment
 */
export const addManualPaymentToApplicationPayment = (applicationPaymentId, paymentData) => {
  return api.post(`${CRM_BASE_URL}/application-payments/${applicationPaymentId}/add-manual-payment`, paymentData);
};

/**
 * Get student credit balance and recent transaction history
 * @param {string} studentId - Student ID
 * @param {Object} [params] - Optional pagination params (page, limit)
 * @returns {Promise} Credit balance and transactions
 */
export const getStudentCreditBalance = (studentId, params = {}) => {
  return api.get(`${CRM_BASE_URL}/students/${studentId}/credit-balance`, { params });
};

/**
 * Verify ApplicationPayment (after payment-submitted status)
 * @param {string} applicationPaymentId - Application payment ID
 * @param {Object} data - Verification data (notes)
 * @returns {Promise} Verified payment data
 */
/** Staff: email (and return) a secure link for the applicant to upload the scholarship proof. */
export const requestApplicationScholarshipProofLink = (applicationId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/applications/${applicationId}/scholarship-proof-link`, data);
};

/** Staff: approve / reject the scholarship proof of an application payment. */
export const reviewApplicationScholarshipProof = (applicationPaymentId, data) => {
  return api.post(`${CRM_BASE_URL}/application-payments/${applicationPaymentId}/scholarship-proof/review`, data);
};

/** Public: scholarship proof upload page (secure token). */
export const getScholarshipProofRequestByToken = (token) => {
  return api.get(`${CRM_BASE_URL}/applications/public/scholarship-proof/${token}`, { skipErrorToast: true });
};

export const submitScholarshipProofByToken = (token, proof) => {
  return api.post(`${CRM_BASE_URL}/applications/public/scholarship-proof/${token}`, { proof }, { skipErrorToast: true });
};

export const verifyApplicationPayment = (applicationPaymentId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/application-payments/${applicationPaymentId}/verify`, data);
};

/**
 * Reject ApplicationPayment verification (after payment-submitted status)
 * @param {string} applicationPaymentId - Application payment ID
 * @param {Object} data - { reason, rejectMode }
 *   rejectMode: 'reverse_and_stop' | 'reverse_and_reopen' | 'keep_and_discontinue'
 * @returns {Promise} Rejected payment data
 */
export const rejectApplicationPaymentVerification = (applicationPaymentId, data) => {
  return api.post(`${CRM_BASE_URL}/application-payments/${applicationPaymentId}/reject-payment`, data);
};

// ============================================================================
// PAYMENT VERIFICATION MANAGEMENT
// ============================================================================

/**
 * Get payment verification by application
 * Returns ApplicationPayment if not yet approved, otherwise PaymentVerification
 * @param {string} applicationId - Application ID
 * @returns {Promise} Payment verification data (or ApplicationPayment if pending approval)
 */
export const getPaymentVerificationByApplication = (applicationId) => {
  return api.get(`${CRM_BASE_URL}/payment-verification/application/${applicationId}`);
};

/**
 * Generate payment link
 * @param {string} paymentVerificationId - Payment Verification ID
 * @returns {Promise} Payment link details
 */
export const generatePaymentLink = (paymentVerificationId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/payment-verification/${paymentVerificationId}/generate-payment-link`, data);
};

/**
 * Mark criteria as met
 * @param {string} eligibilityId - Eligibility ID
 * @param {Object} data - Criteria data with criteriaName and notes
 * @returns {Promise} Success response
 */
export const markCriteriaMet = (eligibilityId, data) => {
  return api.post(`${CRM_BASE_URL}/eligibility/${eligibilityId}/criteria/${data.criteriaName}/met`, { notes: data.notes });
};

/**
 * Mark criteria as not met
 * @param {string} eligibilityId - Eligibility ID
 * @param {Object} data - Criteria data with criteriaName and notes
 * @returns {Promise} Success response
 */
export const markCriteriaNotMet = (eligibilityId, data) => {
  return api.post(`${CRM_BASE_URL}/eligibility/${eligibilityId}/criteria/${data.criteriaName}/not-met`, { notes: data.notes });
};

/**
 * Update document check status
 * @param {string} eligibilityId - Eligibility ID
 * @param {Object} checkData - Document check data with documentType, isVerified, and notes
 * @returns {Promise} Success response
 */
export const updateDocumentCheck = (eligibilityId, checkData) => {
  return api.patch(`${CRM_BASE_URL}/eligibility/${eligibilityId}/documents`, {
    documentType: checkData.documentType,
    isVerified: checkData.isVerified,
    notes: checkData.notes
  });
};

/**
 * Complete eligibility check
 * @param {string} eligibilityId - Eligibility ID
 * @param {Object} completionData - Completion data with decision and notes
 * @returns {Promise} Success response
 */
export const completeEligibilityCheck = (eligibilityId, completionData) => {
  return api.post(`${CRM_BASE_URL}/eligibility/${eligibilityId}/complete`, completionData);
};

/**
 * Submit payment for verification
 * @param {string} applicationId - Application ID
 * @param {Object} paymentData - Payment data
 * @returns {Promise} Payment verification response
 */
export const submitPayment = (applicationId, paymentData) => {
  return api.post(`${CRM_BASE_URL}/payment-verification/application/${applicationId}/submit`, paymentData);
};

/**
 * Verify payment
 * @param {string} paymentId - Payment verification ID
 * @param {Object} verificationData - Verification data
 * @returns {Promise} Success response
 */
export const verifyPayment = (paymentId, verificationData) => {
  return api.post(`${CRM_BASE_URL}/payment-verification/${paymentId}/verify`, verificationData);
};

/**
 * Reject payment
 * @param {string} paymentId - Payment verification ID
 * @param {Object} rejectionData - Rejection data with reason
 * @returns {Promise} Success response
 */
export const rejectPayment = (paymentId, rejectionData) => {
  return api.post(`${CRM_BASE_URL}/payment-verification/${paymentId}/reject`, rejectionData);
};

/**
 * Approve a specific partial payment
 * @param {string} paymentVerificationId - Payment verification ID
 * @param {string} partialPaymentId - Partial payment ID to approve
 * @param {Object} data - Approval data with optional notes
 * @returns {Promise} Success response
 */
export const approvePartialPayment = (paymentVerificationId, partialPaymentId, data = {}) => {
  return api.post(
    `${CRM_BASE_URL}/payment-verification/${paymentVerificationId}/partial-payments/${partialPaymentId}/approve`,
    data
  );
};

/**
 * Reject a specific partial payment
 * @param {string} paymentVerificationId - Payment verification ID
 * @param {string} partialPaymentId - Partial payment ID to reject
 * @param {Object} data - Rejection data with reason (required)
 * @returns {Promise} Success response
 */
export const rejectPartialPayment = (paymentVerificationId, partialPaymentId, data) => {
  return api.post(
    `${CRM_BASE_URL}/payment-verification/${paymentVerificationId}/partial-payments/${partialPaymentId}/reject`,
    data
  );
};

/**
 * Approve full payment (for non-installment payments - legacy single payment)
 * @param {string} paymentVerificationId - Payment verification ID
 * @param {Object} data - Approval data with optional notes
 * @returns {Promise} Success response
 */
export const approveFullPayment = (paymentVerificationId, data = {}) => {
  return api.post(
    `${CRM_BASE_URL}/payment-verification/${paymentVerificationId}/approve-payment`,
    data
  );
};

/**
 * Approve a specific partial payment for full payment (non-installment)
 * @param {string} paymentVerificationId - Payment verification ID
 * @param {string} partialPaymentId - Partial payment ID to approve
 * @param {Object} data - Approval data with optional notes
 * @returns {Promise} Success response
 */
export const approveFullPaymentPartial = (paymentVerificationId, partialPaymentId, data = {}) => {
  return api.post(
    `${CRM_BASE_URL}/payment-verification/${paymentVerificationId}/full-payment/${partialPaymentId}/approve`,
    data
  );
};

/**
 * Reject a specific partial payment for full payment (non-installment)
 * @param {string} paymentVerificationId - Payment verification ID
 * @param {string} partialPaymentId - Partial payment ID to reject
 * @param {Object} data - Rejection data with reason
 * @returns {Promise} Success response
 */
export const rejectFullPaymentPartial = (paymentVerificationId, partialPaymentId, data) => {
  return api.post(
    `${CRM_BASE_URL}/payment-verification/${paymentVerificationId}/full-payment/${partialPaymentId}/reject`,
    data
  );
};

/**
 * Add payment document
 * @param {string} paymentId - Payment verification ID
 * @param {FormData} documentData - Document form data
 * @returns {Promise} Success response
 */
export const addPaymentDocument = (paymentId, documentData) => {
  return api.post(`${CRM_BASE_URL}/payment-verification/${paymentId}/document`, documentData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
};

/**
 * Generate document link for application
 * @param {string} applicationId - Application ID
 * @param {string} documentType - Document type
 * @returns {Promise} Document link response
 */
export const generateDocumentLink = (applicationId, documentType) => {
  return api.post(`${CRM_BASE_URL}/applications/${applicationId}/generate-document-link`, { documentType });
};

/**
 * Resend document submission link by email and SMS
 * @param {string} applicationId - Application ID
 * @returns {Promise} Notification send response
 */
export const sendDocumentLinkEmail = (applicationId) => {
  return api.post(`${CRM_BASE_URL}/applications/${applicationId}/send-document-link-email`);
};

/**
 * Extend document submission link expiry by 30 days (always sends email + SMS)
 * @param {string} applicationId - Application ID
 * @returns {Promise} Extend response
 */
export const extendDocumentLink = (applicationId) => {
  return api.post(`${CRM_BASE_URL}/applications/${applicationId}/extend-document-link`);
};

/**
 * Generate processing fee payment link
 * @param {string} applicationId - Application ID
 * @returns {Promise} Processing fee payment link
 */
export const generateProcessingFeeLink = (applicationId) => {
  return api.post(`${CRM_BASE_URL}/applications/${applicationId}/generate-processing-fee-link`);
};

/**
 * Get application by processing fee token (public)
 * @param {string} token - Processing fee payment token
 * @returns {Promise} Application data
 */
export const getApplicationByProcessingFeeToken = (token) => {
  return api.get(`${CRM_BASE_URL}/applications/public/processing-fee/${token}`);
};

/**
 * Submit processing fee payment (public)
 * @param {string} token - Processing fee payment token
 * @param {Object} paymentData - Payment data
 * @returns {Promise} Success response
 */
export const submitProcessingFeePayment = (token, paymentData) => {
  return api.post(`${CRM_BASE_URL}/applications/public/processing-fee/${token}/submit`, paymentData);
};

/**
 * Verify processing fee payment (staff only)
 * @param {string} applicationId - Application ID
 * @param {Object} data - Verification notes
 * @returns {Promise} Success response
 */
export const verifyProcessingFeePayment = (applicationId, data) => {
  return api.post(`${CRM_BASE_URL}/applications/${applicationId}/verify-processing-fee`, data);
};;

/**
 * Approve a specific uploaded application document (staff)
 * Permission: CRM:APPLICATION_DOCUMENTS:APPROVE
 * @param {string} applicationId
 * @param {string} fileId
 * @param {Object} data - optional { notes }
 */
export const approveApplicationDocument = (applicationId, fileId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/applications/${applicationId}/documents/${fileId}/approve`, data);
};

/**
 * Reject (remove) a specific uploaded application document (staff)
 * Permission: CRM:APPLICATION_DOCUMENTS:REJECT
 * @param {string} applicationId
 * @param {string} fileId
 * @param {Object} data - Optional body e.g. { reason: 'Document is unclear' }
 */
export const rejectApplicationDocument = (applicationId, fileId, data = {}) => {
  return api.post(`${CRM_BASE_URL}/applications/${applicationId}/documents/${fileId}/reject`, data);
};

/**
 * Get eligibility by ID
 * @param {string} eligibilityId - Eligibility ID
 * @returns {Promise} Eligibility data
 */
export const getEligibilityById = (eligibilityId) => {
  return api.get(`${CRM_BASE_URL}/eligibility/${eligibilityId}`);
};

/**
 * Update eligibility criteria
 * @param {string} eligibilityId - Eligibility ID
 * @param {Object} criteriaData - Criteria update data
 * @returns {Promise} Success response
 */
export const updateEligibilityCriteria = (eligibilityId, criteriaData) => {
  return api.put(`${CRM_BASE_URL}/eligibility/${eligibilityId}/criteria`, criteriaData);
};

/**
 * Get payment verification by ID
 * @param {string} paymentVerificationId - Payment verification ID
 * @returns {Promise} Payment verification data
 */
export const getPaymentVerificationById = (paymentVerificationId) => {
  return api.get(`${CRM_BASE_URL}/payment-verification/${paymentVerificationId}`);
};

/**
 * Get all payment verifications with filters
 * @param {Object} filters - Filter parameters
 * @returns {Promise} Payment verifications list
 */
export const getPaymentVerifications = (filters = {}) => {
  return api.get(`${CRM_BASE_URL}/payment-verification`, { params: filters });
};

/**
 * Add payment communication
 * @param {string} paymentVerificationId - Payment verification ID
 * @param {Object} communicationData - Communication data
 * @returns {Promise} Success response
 */
export const addPaymentCommunication = (paymentVerificationId, communicationData) => {
  return api.post(`${CRM_BASE_URL}/payment-verification/${paymentVerificationId}/communication`, communicationData);
};

// Export all functions as default object

// ===== Student transfers (move a student to another programme, carrying payments) =====
export const getEnrollmentTransferOptions = (id, params = {}) => {
  return api.get(`${CRM_BASE_URL}/enrollments/${id}/transfer/options`, { params });
};

export const previewEnrollmentTransfer = (id, payload) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/transfer/preview`, payload);
};

export const requestEnrollmentTransfer = (id, payload) => {
  return api.post(`${CRM_BASE_URL}/enrollments/${id}/transfer`, payload);
};

export const getEnrollmentTransfers = (id) => {
  return api.get(`${CRM_BASE_URL}/enrollments/${id}/transfers`);
};

export const listEnrollmentTransfers = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/enrollments/transfers`, { params });
};

export const approveEnrollmentTransfer = (transferId) => {
  return api.post(`${CRM_BASE_URL}/enrollments/transfers/${transferId}/approve`);
};

export const rejectEnrollmentTransfer = (transferId, payload = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/transfers/${transferId}/reject`, payload);
};

export const cancelEnrollmentTransfer = (transferId) => {
  return api.post(`${CRM_BASE_URL}/enrollments/transfers/${transferId}/cancel`);
};

export const retryEnrollmentTransferFinance = (transferId) => {
  return api.post(`${CRM_BASE_URL}/enrollments/transfers/${transferId}/retry-finance`);
};

export const getEnrollmentTransferUndoCheck = (transferId) => {
  return api.get(`${CRM_BASE_URL}/enrollments/transfers/${transferId}/undo-check`);
};

export const undoEnrollmentTransfer = (transferId, payload = {}) => {
  return api.post(`${CRM_BASE_URL}/enrollments/transfers/${transferId}/undo`, payload);
};


// Application-stage transfer (programme and/or branch, before the application is approved)
export const getApplicationTransferOptions = (id, params = {}) => {
  return api.get(`${CRM_BASE_URL}/applications/${id}/transfer/options`, { params });
};

export const previewApplicationTransfer = (id, payload) => {
  return api.post(`${CRM_BASE_URL}/applications/${id}/transfer/preview`, payload);
};

export const requestApplicationTransfer = (id, payload) => {
  return api.post(`${CRM_BASE_URL}/applications/${id}/transfer`, payload);
};

export const getApplicationTransfers = (id) => {
  return api.get(`${CRM_BASE_URL}/applications/${id}/transfers`);
};

const crmApi = {
  // Customers
  getCustomers,
  getAllCustomers,
  getCustomerById,
  createCustomer,
  updateCustomer,
  archiveCustomer,
  deleteCustomer,
  getCustomerStats,
  getCustomerContacts,
  getContactsByCustomer,
  getAllContacts,
  addCustomerContact,
  
  // Leads
  getLeads,
  getAllLeads,
  getMyLeads,
  getLeadById,
  createLead,
  bulkUploadLeads,
  updateLead,
  convertLead,
  qualifyLead,
  archiveLead,
  bulkDeleteLeads,
  assignLead,
  bulkAssignLeads,
  addLeadActivity,
  editLeadActivity,
  validateLeadUniqueness,
  lookupStudent,
  
  // Lead Pools
  getLeadPools,
  getActiveLeadPools,
  getLeadPoolsByType,
  getLeadPoolById,
  getLeadPoolTransferredOut,
  createLeadPool,
  updateLeadPool,
  deleteLeadPool,
  addLeadsToPool,
  removeLeadsFromPool,
  removeLeadFromPool,
  transferLeadsToPool,
  getLeadPoolStatistics,
  
  // Opportunities
  getOpportunities,
  getOpportunityById,
  getOpportunityStats,
  createOpportunity,
  updateOpportunity,
  moveOpportunityStage,
  closeOpportunity,
  
  // Training
  getTrainings,
  getTrainingById,
  createTraining,
  updateTraining,
  deleteTraining,
  getTrainingProducts,
  getTrainingProductById,
  createTrainingProduct,
  getTrainingSessions,
  registerTrainingParticipant,
  registerForTraining,
  markTrainingAttendance,
  cancelTrainingRegistration,
  generateTrainingCertificate,
  
  // Workshops
  getWorkshops,
  getWorkshopById,
  createWorkshop,
  updateWorkshop,
  deleteWorkshop,
  registerForWorkshop,
  workshopCheckIn,
  cancelWorkshopRegistration,
  getWorkshopRegistrationByQR,
  generateWorkshopQRCode,
  convertWorkshopAttendeesToLeads,
  generateWorkshopPaymentLink,
  verifyWorkshopPayment,
  
  // Quotations
  getQuotations,
  getQuotationById,
  createQuotation,
  sendQuotation,
  convertQuotationToOrder,
  generateQuotationPDF,
  
  // Campaigns
  getCampaigns,
  getCampaignById,
  createCampaign,
  launchCampaign,
  getCampaignAnalytics,
  
  // Support
  getSupportTickets,
  getSupportTicketById,
  createSupportTicket,
  addTicketComment,
  updateTicketStatus,
  
  // Contracts
  getContracts,
  getContractById,
  createContract,
  sendContractForSignature,
  renewContract,
  
  // Analytics
  getCRMDashboard,
  getSalesPipeline,
  getCustomerInsights,
  getRevenueForecast,
  exportCRMReport,
  
  // Educational Product Management
  getCategories,
  getCategoryById,
  createCategory,
  updateCategory,
  deleteCategory,
  getProducts,
  getProductById,
  createProduct,
  updateProduct,
  deleteProduct,
  getSubProducts,
  getSubProductById,
  createSubProduct,
  updateSubProduct,
  deleteSubProduct,
  getSubProductSchedule,
  calculateSubProductPricing,
  generateSpecialDiscountCodes,
  getSubProductAnalytics,
  searchSubProducts,
  
  // Intakes
  getIntakes,
  getIntakeById,
  createIntake,
  updateIntake,
  deleteIntake,
  getIntakesBySubProduct,
  getIntakesByMainProduct,
  calculateIntakePricing,
  updateIntakeStatus,
  getIntakeStatistics,
  addIntakeEnrollment,
  removeIntakeEnrollment,
  
  // Enrollments
  getEnrollments,
  getEnrollmentById,
  searchEnrollmentCandidates,
  createEnrollment,
  updateEnrollment,
  deleteEnrollment,
  approveEnrollment,
  confirmEnrollment,
  recordEnrollmentPayment,
  generateEnrollmentPaymentLink,
  generateEnrollmentDocumentLink,
  getEnrollmentByDocumentToken,
  deletePublicEnrollmentDocument,
  approveEnrollmentDocument,
  rejectEnrollmentDocument,
  reviewEnrollmentScholarshipProof,
  requestApplicationScholarshipProofLink,
  reviewApplicationScholarshipProof,
  getScholarshipProofRequestByToken,
  submitScholarshipProofByToken,
  addManualEnrollmentPayment,
  approveManualEnrollmentPayment,
  printEnrollmentPaymentReceipt,
  emailEnrollmentPaymentReceipt,
  getAvailableCreditNotesForEnrollment,
  rejectManualEnrollmentPayment,
  reverseManualEnrollmentPayment,
  getEnrollmentRepeatCharges,
  createEnrollmentExamRepeatCharge,
  createEnrollmentModuleRepeatCharge,
  batchSendEnrollmentModuleRepeatCharges,
  createEnrollmentModuleMoveCharge,
  batchSendEnrollmentExamResitCharges,
  createEnrollmentIntakeRepeatCharge,
  approveEnrollmentRepeatCharge,
  cancelEnrollmentRepeatCharge,
  recordEnrollmentRepeatChargePayment,
  reverseEnrollmentRepeatChargePayment,
  approveEnrollmentRepeatChargePayment,
  rejectEnrollmentRepeatChargePayment,
  initiateEnrollmentRepeatChargeCardPayment,
  createEnrollmentRepeatEnrollment,
  recordEnrollmentAttendance,
  submitEnrollmentFeedback,
  withdrawEnrollment,
  deferEnrollment,
  issueEnrollmentCertificate,
  getEnrollmentsByStudent,
  getEnrollmentsByIntake,
  getEnrollmentStatistics,
  
  // Graduations
  getGraduations,
  getGraduationById,
  createGraduation,
  updateGraduation,
  deleteGraduation,
  restoreGraduation,
  getEligibleStudents,
  addStudentsToGraduation,
  removeStudentFromGraduation,
  confirmStudentAttendance,
  issueCertificateToStudent,
  updateGraduationStatus,
  getGraduationStatistics,
  getUpcomingGraduations,
  getPastGraduations,
  approveGraduation,
  markStudentAbsent,
  batchUpdateAttendance,
  refundNonAttendeeOverride,
  generateGraduationDepositLinks,
  resendGraduationDepositLink,
  getGraduationDepositPayments,
  getGraduationDepositPaymentById,
  approveGraduationDepositPayment,
  rejectGraduationDepositPayment,
  reverseGraduationDepositPayment,
  printGraduationDepositReceipt,
  emailGraduationDepositReceipt,
  getGraduationDepositSummary,
  waiveGraduationDeposit,
  addStaffManualGraduationDepositPayment,
  getPublicGraduationDepositDetails,
  submitPublicGraduationDepositPayment,
  initiatePublicGraduationDepositCardPayment,
  
  // Students
  getAllStudents,
  getStudentById,
  getStudentByNumber,
  getProspectiveStudents,
  getActiveStudents,
  createStudent,
  updateStudent,
  updateStudentStatus,
  verifyStudentDocuments,
  getStudentEnrollments,
  deleteStudent,
  getStudentReport,
  exportStudentReportCsv,
  exportEligibilityQuestionsReport,
  getEligibilityQuestionsSummary,
  getModuleEnrollmentMatrix,
  exportModuleEnrollmentMatrix,
  getModuleEnrollmentMatrixOptions,
  getStudentCreditReport,
  exportStudentCreditReportCsv,
  getLeadReport,
  exportLeadReportCsv,
  downloadLeadReportPdf,
  getExtendedCRMReport,
  exportExtendedCRMReportCsv,
  getExportTemplates,
  previewDynamicExport,
  downloadDynamicExport,
  downloadAllProductsExport,
  getPaymentReport,
  exportPaymentReportCsv,
  downloadPaymentReportPdf,
  getStudentAccountStatement,
  exportStudentAccountStatementCsv,
  downloadStudentAccountStatementPdf,

  // Applications
  createApplication,
  getApplicationMembershipFeeOption,
  updateApplicationMembershipFeeOption,
  updateApplicationModuleSelections,
  updateApplicationDiscountEligibility,
  getApplicationById,
  getApplicationByToken,
  getApplicationByNumber,
  getApplicationForLeadWorkflow,
  getLeadApplicationLinkReadiness,
  sendLeadApplicationLinkEmail,
  getApplications,
  getApplicationSubStatusCounts,
  updateApplication,
  updateApplicationIdentity,
  changeApplicationRequirementCategory,
  changeEnrollmentRequirementCategory,
  getEnrollmentTransferOptions,
  previewEnrollmentTransfer,
  requestEnrollmentTransfer,
  getEnrollmentTransfers,
  listEnrollmentTransfers,
  approveEnrollmentTransfer,
  rejectEnrollmentTransfer,
  cancelEnrollmentTransfer,
  retryEnrollmentTransferFinance,
  getEnrollmentTransferUndoCheck,
  undoEnrollmentTransfer,
  getApplicationTransferOptions,
  previewApplicationTransfer,
  requestApplicationTransfer,
  getApplicationTransfers,
  submitApplication,
  generateDocumentLink,
  sendDocumentLinkEmail,
  extendDocumentLink,
  approveApplication,
  rejectApplication,
  getApplicationLifecycleContext,
  cancelApplicationLifecycle,
  restructureApplicationPaymentLifecycle,
  deleteApplication,

  // Application Payments
  getApplicationPaymentByApplication,
  getPublicApplicationPaymentByApplication,
  listApplicationPayments,
  approveApplicationPayment,
  rejectApplicationPayment,
  addManualPaymentToApplicationPayment,
  getAvailableCreditNotesForApplicationPayment,
  getStudentCreditBalance,

  // Eligibility Checks
  getEligibilityByApplication,
  getEligibilityById,
  updateEligibilityCriteria,
  markCriteriaMet,
  markCriteriaNotMet,
  updateDocumentCheck,
  completeEligibilityCheck,

  // Payment Verification
  getPaymentVerificationByApplication,
  getPaymentVerificationById,
  getPaymentVerifications,
  submitPayment,
  verifyPayment,
  rejectPayment,
  approvePartialPayment,
  rejectPartialPayment,
  approveFullPayment,
  approveFullPaymentPartial,
  rejectFullPaymentPartial,
  addPaymentDocument,
  addPaymentCommunication,
  generatePaymentLink,
  
  // Refund Management
  getRefunds,
  getAllRefunds,
  getRefundById,
  createRefund,
  updateRefund,
  deleteRefund,
  approveRefund,
  rejectRefund,
  processRefund,
  cancelRefund,
  getRefundsByStudent,
  getRefundsByEnrollment,
  getRefundStatistics,

  // Credit Note Management
  getCreditNotes,
  getCreditNoteById,
  createCreditNote,
  updateCreditNote,
  deleteCreditNote,
  approveCreditNote,
  rejectCreditNote,
  cancelCreditNote,
  getCreditNotesByStudent,
  getStudentPaymentsForCreditNote,
  getCreditNoteStatistics,
  writeOffCreditNoteBalance,
  getCreditNotesByInvoice,

  // Report Targets
  getReportTargets,
  getReportTargetsBySubProduct,
  updateReportTargets,
};

export default crmApi;

// ============================================================================
// CRM SETTINGS
// ============================================================================

/**
 * Get a CRM setting by key
 * @param {string} key - Setting key
 * @returns {Promise} Setting value
 */
export const getCrmSetting = (key) => {
  return api.get(`${CRM_BASE_URL}/settings/${key}`);
};

/**
 * Global bank receiving accounts by location.
 * Readable by any authenticated CRM user (used by lead/application flows).
 */
export const getLocationBankReceivingAccountsSetting = () => {
  return api.get(`${CRM_BASE_URL}/settings/locationBankReceivingAccounts`);
};

/**
 * Global education finance mappings (standard COA defaults for subproducts).
 * Readable by any authenticated CRM user (used by subproduct/application flows).
 */
export const getEducationFinanceMappingsSetting = () => {
  return api.get(`${CRM_BASE_URL}/settings/educationFinanceMappings`);
};

/**
 * Save a CRM setting by key (requires CRM:SETTINGS:UPDATE permission)
 * @param {string} key - Setting key
 * @param {*} value - Value to store
 * @returns {Promise} Saved value
 */
export const putCrmSetting = (key, value) => {
  return api.put(`${CRM_BASE_URL}/settings/${key}`, { value });
};

/**
 * Get a public CRM policy setting by key — no auth required.
 * Only 'enrollmentTermsAndConditions' and 'institutionPaymentPolicy' are accessible.
 * @param {string} key - Setting key
 * @returns {Promise} Setting value
 */
export const getCrmPublicSetting = (key) => {
  return api.get(`${CRM_BASE_URL}/public/policy/${key}`);
};

// ============================================================================
// FRANCHISE CENTERS
// ============================================================================

export const getCenterCommercialConfigs = (params = {}) =>
  api.get(`${CRM_BASE_URL}/centers/commercial-configs`, { params });

export const upsertCenterCommercialConfig = (payload) =>
  api.post(`${CRM_BASE_URL}/centers/commercial-configs`, payload);

export const getCenterVolumeDiscounts = (params = {}) =>
  api.get(`${CRM_BASE_URL}/centers/volume-discounts`, { params });

export const upsertCenterVolumeDiscount = (payload) =>
  api.post(`${CRM_BASE_URL}/centers/volume-discounts`, payload);

export const getCenterSettlementEntries = (params = {}) =>
  api.get(`${CRM_BASE_URL}/centers/settlement-entries`, { params });

export const getCenterSettlementInvoices = (params = {}) =>
  api.get(`${CRM_BASE_URL}/centers/settlement-invoices`, { params });

export const getCenterSettlementPayments = (params = {}) =>
  api.get(`${CRM_BASE_URL}/centers/settlement-payments`, { params });

export const generateCenterSettlementInvoice = (payload) =>
  api.post(`${CRM_BASE_URL}/centers/settlement-invoices/generate`, payload);

export const previewCenterSettlementInvoice = (payload) =>
  api.post(`${CRM_BASE_URL}/centers/settlement-invoices/preview`, payload);

export const requestCenterSettlementInvoice = (payload) =>
  api.post(`${CRM_BASE_URL}/centers/settlement-invoices/request`, payload);

export const previewCenterSettlementInvoiceById = (id) =>
  api.post(`${CRM_BASE_URL}/centers/settlement-invoices/${id}/preview`);

export const issueCenterSettlementInvoice = (id, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/settlement-invoices/${id}/issue`, payload);

export const rejectCenterSettlementInvoiceRequest = (id, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/settlement-invoices/${id}/reject-request`, payload);

export const getCenterInvoiceEligibleApplications = (params = {}) =>
  api.get(`${CRM_BASE_URL}/centers/invoice-eligible-applications`, { params });

export const getCenterCourseVolumeProgress = (params = {}) =>
  api.get(`${CRM_BASE_URL}/centers/course-volume-progress`, { params });

export const generateCenterSettlementPaymentLink = (id, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/settlement-invoices/${id}/generate-payment-link`, payload);

export const submitCenterSettlementManualPayment = (id, payload) =>
  api.post(`${CRM_BASE_URL}/centers/settlement-invoices/${id}/manual-payment`, payload);

export const approveCenterSettlementPayment = (id, paymentId, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/settlement-invoices/${id}/payments/${paymentId}/approve`, payload);

export const rejectCenterSettlementPayment = (id, paymentId, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/settlement-invoices/${id}/payments/${paymentId}/reject`, payload);

export const reverseCenterSettlementPayment = (id, paymentId, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/settlement-invoices/${id}/payments/${paymentId}/reverse`, payload);

export const downloadCenterSettlementInvoicePdf = (id) =>
  api.get(`${CRM_BASE_URL}/centers/settlement-invoices/${id}/pdf`, { responseType: 'blob' });

/** @deprecated Prefer printCenterSettlementReceipt — kept for compatibility */
export const downloadCenterSettlementReceiptPdf = (id, paymentId, { variant } = {}) =>
  api.get(`${CRM_BASE_URL}/centers/settlement-invoices/${id}/payments/${paymentId}/receipt-pdf`, {
    responseType: 'blob',
    params: variant ? { variant } : undefined,
  });

export const printCenterSettlementReceipt = (id, paymentId, { variant } = {}) =>
  api.get(`${CRM_BASE_URL}/centers/settlement-invoices/${id}/payments/${paymentId}/receipt/print`, {
    responseType: 'blob',
    params: variant ? { variant } : undefined,
  });

export const emailCenterSettlementReceipt = (id, paymentId, data = {}) =>
  api.post(`${CRM_BASE_URL}/centers/settlement-invoices/${id}/payments/${paymentId}/receipt/email`, data);

export const getCenterSettlementFinanceConfig = () =>
  api.get(`${CRM_BASE_URL}/centers/settlement-finance-config`);

export const upsertCenterSettlementFinanceConfig = (payload) =>
  api.put(`${CRM_BASE_URL}/centers/settlement-finance-config`, payload);

export const getPublicCenterSettlementPayment = (token) =>
  api.get(`${CRM_BASE_URL}/centers/public/settlement-pay/${token}`);

export const downloadPublicCenterSettlementInvoicePdf = (token) =>
  api.get(`${CRM_BASE_URL}/centers/public/settlement-pay/${token}/pdf`, { responseType: 'blob' });

export const submitPublicCenterSettlementPayment = (token, payload) =>
  api.post(`${CRM_BASE_URL}/centers/public/settlement-pay/${token}`, payload);

export const initiatePublicCenterSettlementCardPayment = (token, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/public/settlement-pay/${token}/initiate-card`, payload);

export const submitCenterSettlementTransfer = (id, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/settlement-invoices/${id}/submit-transfer`, payload);

export const confirmCenterSettlementInvoice = (id) =>
  api.post(`${CRM_BASE_URL}/centers/settlement-invoices/${id}/confirm`);

export const getCenterRegionMappings = () =>
  api.get(`${CRM_BASE_URL}/centers/region-mappings`);

export const upsertCenterRegionMapping = (payload) =>
  api.post(`${CRM_BASE_URL}/centers/region-mappings`, payload);

export const getCenterSelfRegistrations = (params = {}) => {
  if (params.format === 'csv' || params.format === 'pdf') {
    return api.get(`${CRM_BASE_URL}/centers/self-registrations`, {
      params,
      responseType: 'blob'
    });
  }
  return api.get(`${CRM_BASE_URL}/centers/self-registrations`, { params });
};

export const getCenterSelfRegistrationById = (id) =>
  api.get(`${CRM_BASE_URL}/centers/self-registrations/${id}`);

export const updateCenterSelfRegistration = (id, payload) =>
  api.put(`${CRM_BASE_URL}/centers/self-registrations/${id}`, payload);

export const sendCenterSelfRegistrationLink = (payload) =>
  api.post(`${CRM_BASE_URL}/centers/self-registration-link/send`, payload);

export const getMyCrmNotifications = (params = {}) =>
  api.get(`${CRM_BASE_URL}/notifications`, { params });

export const markCrmNotificationRead = (id) =>
  api.post(`${CRM_BASE_URL}/notifications/${id}/read`);

export const markAllCrmNotificationsRead = () =>
  api.post(`${CRM_BASE_URL}/notifications/read-all`);

export const retryCenterLmsProvision = (studentId, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/students/${studentId}/lms-provision`, payload);

export const getCenterStudentBatches = (params = {}) => {
  if (params.format === 'csv' || params.format === 'pdf') {
    return api.get(`${CRM_BASE_URL}/centers/student-batches`, {
      params,
      responseType: 'blob'
    });
  }
  return api.get(`${CRM_BASE_URL}/centers/student-batches`, { params });
};

export const getCenterStudentBatchById = (id, params = {}) =>
  api.get(`${CRM_BASE_URL}/centers/student-batches/${id}`, { params });

export const getCenterVolumeProgress = (params = {}) =>
  api.get(`${CRM_BASE_URL}/centers/volume-progress`, { params });

export const ensureCenterDraftBatch = (payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/student-batches/draft`, payload);

export const submitCenterStudentBatch = (id, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/student-batches/${id}/submit`, payload);

export const updateCenterStudentBatchMembers = (id, payload = {}) =>
  api.patch(`${CRM_BASE_URL}/centers/student-batches/${id}/members`, payload);

export const submitSelectedCenterSelfRegistrations = (payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/student-batches/submit-selected`, payload);

export const verifyCenterStudentBatch = (id, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/student-batches/${id}/verify`, payload);

export const verifyCenterStudentBatchMember = (batchId, registrationId, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/student-batches/${batchId}/members/${registrationId}/verify`, payload);

export const verifyCenterSelfRegistration = (id, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/self-registrations/${id}/verify`, payload);

export const flagCenterSelfRegistration = (id, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/self-registrations/${id}/flag`, payload);

export const rejectCenterStudentBatch = (id, payload = {}) =>
  api.post(`${CRM_BASE_URL}/centers/student-batches/${id}/reject`, payload);

export const getCenterBankProfile = (locationId) =>
  api.get(`${CRM_BASE_URL}/centers/bank-profile/${locationId}`);

export const getCenterHqDashboard = () =>
  api.get(`${CRM_BASE_URL}/centers/hq-dashboard`, { skipErrorToast: true });

export const getPublicCenterSelfRegMeta = (locationId) =>
  api.get(`${CRM_BASE_URL}/centers/public/self-registration/${locationId}`, {
    // Bust browser/axios conditional cache without custom headers (avoids CORS preflight failures).
    params: { _ts: Date.now() }
  });

export const submitPublicCenterSelfRegistration = (locationId, payload) =>
  api.post(`${CRM_BASE_URL}/centers/public/self-registration/${locationId}`, payload);

export const resolvePublicLeadRegion = (params = {}) =>
  api.get(`${CRM_BASE_URL}/centers/public/resolve-region`, { params });

// ─── Module Exemption APIs ────────────────────────────────────────────────────

/**
 * Submit a module exemption fee review request (public, no auth required)
 * @param {string} applicationId - Application _id or applicationNumber
 * @param {string} moduleCode - Module code
 * @param {Array}  supportingDocuments - [{ fileId, fileName, fileSize, fileType }]
 */
export const requestModuleExemption = (applicationId, moduleCode, supportingDocuments) => {
  return api.post(
    `${CRM_BASE_URL}/applications/public/${applicationId}/module-exemption`,
    { moduleCode, supportingDocuments }
  );
};

/**
 * Cancel a pending module exemption request (public, no auth required)
 * @param {string} applicationId - Application _id or applicationNumber
 * @param {string} moduleCode - Module code
 */
export const cancelModuleExemption = (applicationId, moduleCode) => {
  return api.delete(
    `${CRM_BASE_URL}/applications/public/${applicationId}/module-exemption/${encodeURIComponent(moduleCode)}`
  );
};

/**
 * List module exemption requests (admin)
 * @param {Object} params - status, search, studentType, mainProductId, subProductId,
 *   intakeId, moduleCode, dateFrom, dateTo, page, limit, format (csv|pdf)
 */
export const listModuleExemptions = (params = {}) => {
  const { format, ...rest } = params;
  if (format === 'csv' || format === 'pdf') {
    return api.get(`${CRM_BASE_URL}/applications/module-exemptions`, {
      params: { ...rest, format },
      responseType: 'blob'
    });
  }
  return api.get(`${CRM_BASE_URL}/applications/module-exemptions`, { params });
};

/**
 * Review (approve or reject) a module exemption request (admin)
 * @param {string} applicationId - Application _id
 * @param {string} moduleCode - Module code
 * @param {Object} data - { decision: 'approved'|'rejected', reviewNotes?, exemptionFee? }
 */
export const reviewModuleExemption = (applicationId, moduleCode, data) => {
  return api.post(
    `${CRM_BASE_URL}/applications/${applicationId}/module-exemption/${encodeURIComponent(moduleCode)}/review`,
    data
  );
};

// ============================================================================
// AUDIT LOGS REPORTS
// ============================================================================

/**
 * Get paginated/filtered CRM audit logs
 * @param {Object} params - { page, limit, search, method, statusCodeGroup, startDate, endDate }
 * @returns {Promise} Audit logs response
 */
export const getCrmAuditLogs = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/reports/audit-logs`, { params });
};

/**
 * Export CRM audit logs as CSV or PDF
 * @param {Object} params - { format: 'csv'|'pdf', search, method, statusCodeGroup, startDate, endDate }
 * @returns {Promise} Blob response
 */
export const exportCrmAuditLogs = (params = {}) => {
  return api.post(`${CRM_BASE_URL}/reports/audit-logs/export`, params, { responseType: 'blob' });
};

// ============================================================================
// STUDENT INVOICES
// ============================================================================

/**
 * List student invoices
 */
export const getStudentInvoices = (params = {}) => {
  return api.get(`${CRM_BASE_URL}/student-invoices`, { params });
};

/**
 * Get student invoice by ID
 */
export const getStudentInvoiceById = (id) => {
  return api.get(`${CRM_BASE_URL}/student-invoices/${id}`);
};

/**
 * Get student invoice by source (ApplicationPayment or Enrollment)
 */
export const getStudentInvoiceBySource = (sourceType, sourceId) => {
  return api.get(`${CRM_BASE_URL}/student-invoices/by-source/${sourceType}/${sourceId}`, {
    skipErrorToast: true,
  });
};

/**
 * Download student invoice PDF (blob)
 * @param {string} id - Invoice ID
 * @param {string} copyType - 'original' | 'duplicate'
 */
export const downloadStudentInvoicePdf = (id, copyType = 'original') => {
  return api.get(`${CRM_BASE_URL}/student-invoices/${id}/pdf`, {
    params: { copyType },
    responseType: 'blob',
  });
};

/**
 * Email student invoice with PDF attachment
 */
export const sendStudentInvoiceEmail = (id, payload = {}) => {
  return api.post(`${CRM_BASE_URL}/student-invoices/${id}/send-email`, payload);
};

/**
 * Get CRM Invoice Settings (SLIM V.A.T. No.)
 */
export const getCrmInvoiceSettings = () => {
  return api.get(`${CRM_BASE_URL}/settings/crmInvoiceSettings`);
};

/**
 * Update CRM Invoice Settings (SLIM V.A.T. No.)
 */
export const updateCrmInvoiceSettings = (value) => {
  return api.put(`${CRM_BASE_URL}/settings/crmInvoiceSettings`, { value });
};

